/**
 * ProgramModelModal — the Program Model tool (user-facing name "Building Information Model" as
 * of 2026-07; internal file/table/event names unchanged — see the doc note below): the
 * project's program (Units → Levels → Rooms) overlaid on per-level plan PDFs. The "virtual
 * model of the building" and the scaffold future room-level features (product schedules, Revit
 * links) build onto. See docs/PROGRAM_MODEL.md.
 *
 * Opened from a Concept's ⋯ menu via the 'open-program-model-modal' CustomEvent
 * ({ conceptId }); ProgramModelHost is mounted once in OutlineView. Internal team tool only.
 *
 * "Send to Sketch" (2026-07): the top bar's Send to Sketch button hands the active level's
 * already-scaled background plan off to the standalone /sketch tool as a fresh document, opened
 * in a new tab — mirrors ConceptDrawingModal.jsx's own Send to Sketch exactly (same v2 doc
 * shape, same save-then-window.open(id) handoff; see handleSendToSketch/u8ToDataUrl). Requires
 * retaining the level's raw PDF bytes (pdfCacheRef.current[levelId].bytes — getDocument() only
 * ever receives a sliced copy, so the original stays intact for this) since the pdf.js page
 * proxy alone isn't enough to re-embed the PDF in the new sketch.
 *
 * Layout (Round 2 rework): RIGHT panel = the Building tree — Units expand to Levels, Levels
 * expand to Rooms; clicking any node selects it. LEFT panel = the Properties palette for the
 * current selection (deliberately NOT the outline row format — a label/value palette): a room
 * shows its editable identity/area fields plus its budget range; a level or unit shows a
 * ROLL-UP (room count, polygon coverage, net/gross SF, and Budget Low–High summed from each
 * room's Airtable `Budget Room Cost Low/High` formulas — the Control Budget numbers). Both
 * panels are drag-resizable via the dividers; widths persist in localStorage
 * ('aj-pm-propw' / 'aj-pm-treew'). Canvas in the middle = the active level's plan PDF with
 * room polygons (pan/zoom, scale calibration, polygon draw/edit).
 *
 * Canvas engine, coordinate chain, and scale calibration are adapted from
 * TakeoffEditor.jsx — all stored geometry is page-relative VISUAL FRACTIONS [fx,fy] paired with
 * the plan's page_frame fingerprint (lib/pdfFrame.js), so a replaced plan PDF never drifts the
 * polygons. Fractions are frame-independent by construction: on replace they carry over
 * unchanged and only the physical scale (points-per-foot) needs re-deriving.
 *
 * AREA CONTRACT: a room polygon's SF (shoelace ÷ sppf²) writes the Airtable room's
 * `Area Manual` on create/edit/recalibration and CLEARS it on delete — Airtable's
 * `Room Area Net` formula gives Area Manual priority over the conceptual Length × Width.
 * Airtable writes go through services/design.js updateRoom; if one fails the polygon stays
 * (Supabase succeeded) and the room row shows a red "area not synced" badge with Retry.
 *
 * Geometry persists per-edit (takeoff-regions posture, not the site-analysis snapshot model);
 * vertex drags are debounced 600ms per room and flushed on level switch / close.
 */

import { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { loadPdfjs } from '../../lib/pdfjs.js'
import { alertDialog, confirmDialog, promptDialog } from '../Dialog.jsx'
import { isFrame, visualSize, normalizeRotation, framesEqual, aspectsMatch } from '../../lib/pdfFrame.js'
import {
  CATEGORY_PALETTE, withAlpha, polygonCentroid, pointInPolygon,
  distToSeg, computeRegionValue, scalePointsPerFoot, fmtSF, feetLabel,
} from '../takeoff/takeoffGeom.js'
import { usePdfPagePicker } from '../PdfPagePickerModal.jsx'
import { useActionMenu, ActionMenu, ActionMenuButton, ActionMenuItem, ActionMenuDivider } from '../ActionMenu.jsx'
import SearchablePicker from '../SearchablePicker.jsx'
import { ctx } from '../../outline/ctx.js'
import { wf } from '../../outline/state.js'
import { getConceptUnits, getConceptLevels, getLevelRooms, createRoom, updateRoom, deriveCostFactor, STRUCTURAL_CHARACTERISTIC_FACTORS } from '../../services/design.js'
import { RoomFormModal, useRoomTemplates } from '../ConceptRoomsSection.jsx'
import SheetPlanPickerModal from './SheetPlanPickerModal.jsx'
import AddViewModal from './AddViewModal.jsx'
import { SCOPE_TYPE_OPTIONS, SCOPE_TYPE_DEFINITIONS } from '../../outline/constants.js'
import { saveSketch } from '../../services/sketches.js'
import {
  getProgramModel, createPlanUploadUrl, uploadPlanPdf, finalizePlanUpload,
  setPlanScale, saveRoomPolygon, deleteRoomPolygon, setPlanAnchor, setLevelElevation,
} from '../../services/programPlans.js'
import {
  createUnitView, renameView as renameViewSvc, deleteUnitView, updateViewCallout,
  setViewSketch, createViewUploadUrl, uploadViewFile, finalizeViewUpload, reorderUnitViews,
} from '../../services/programViews.js'

const DARK = '#07272D'
const CAL_COLOR = '#1a6fa8'
const SELECT_COLOR = '#ec4899'
const VIEW_COLOR = '#c2410c'
const ANCHOR_COLOR = '#7c3aed'
const DEFAULT_LABEL_FEET_DY = -3   // default label position: 3ft "above" the marker, in world feet
const TARGET_PX = 2800
const MAX_RASTER_DIM = 8192
const MAX_RASTER_AREA = 40 * 1024 * 1024
const RESHARPEN_MS = 220
const HANDLE = 7
const CLOSE_PX = 12
const PANEL_MIN = 220
const PANEL_MAX = 560
const DEFAULT_PROP_W = 300
const DEFAULT_TREE_W = 290
const BAR_CANDIDATES = [1, 2, 5, 10, 20, 25, 50, 100, 200, 500]
const SYNC_DEBOUNCE_MS = 600

function clamp(v, lo, hi) { return Math.max(lo, Math.min(hi, v)) }
const round5 = v => Math.round(v * 100000) / 100000
const roundPt = f => [round5(f[0]), round5(f[1])]

function fmtUSD(v) {
  if (v == null || isNaN(v)) return '—'
  return '$' + Math.round(v).toLocaleString()
}

/** A decimal fraction (e.g. 0.20, -0.15) as a signed whole-percent label ("+20%", "-15%", "0%"). */
function pctLabel(frac) {
  const pct = Math.round((Number(frac) || 0) * 100)
  return (pct > 0 ? '+' : '') + pct + '%'
}

function loadPanelW(key, fallback) {
  try { const v = parseInt(localStorage.getItem(key), 10); if (v > 0) return clamp(v, PANEL_MIN, PANEL_MAX) } catch { /* noop */ }
  return fallback
}

/** Stable per-room color — hash the record id into the takeoff palette. */
function roomColor(roomId) {
  let h = 0
  for (const ch of String(roomId)) h = (h * 31 + ch.charCodeAt(0)) >>> 0
  return CATEGORY_PALETTE[h % CATEGORY_PALETTE.length]
}

function roomDisplayName(room) {
  const f = room.fields || {}
  return f['Room'] || f['Custom Name'] || 'Room'
}

/** The properties palette's editable-field snapshot for a room — used both to seed the local
 *  draft and, recomputed fresh each render, as the "last saved" baseline the draft is diffed
 *  against to decide whether there are unsaved changes. Numeric fields are kept as strings
 *  (raw input value) so an in-progress edit like "12." doesn't get mangled by Number() round-
 *  tripping before the user is done typing. */
function roomDraft(room) {
  const f = room?.fields || {}
  return {
    customName: f['Custom Name'] || '',
    roomTemplateId: (f['Room Template'] || [])[0] || '',
    scopeType: f['Scope Type'] || '',
    roomNumber: f['Room Number'] || '',
    length: f['Length'] != null ? String(f['Length']) : '',
    width: f['Width'] != null ? String(f['Width']) : '',
    areaManual: f['Area Manual'] != null ? String(f['Area Manual']) : '',
    structuralCharacteristics: f['Structural Characteristics'] || [],
  }
}

function scaleFromPlan(plan) {
  const sp = plan?.scalePoints
  const sppf = Number(plan?.scalePointsPerFoot)
  if (!sp || !Array.isArray(sp.p1) || !Array.isArray(sp.p2) || !(sppf > 0)) return null
  return { p1: [...sp.p1], p2: [...sp.p2], feet: Number(sp.feet) || 0, sppf }
}

// Mirrors ConceptDrawingModal.jsx's handleSendToSketch — same v2 sketch-doc shape, same
// save-then-open-in-new-tab handoff (the new tab's only payload is the fresh sketch's own id).
function u8ToDataUrl(u8) {
  let s = ''
  for (let i = 0; i < u8.length; i += 0x8000) s += String.fromCharCode.apply(null, u8.subarray(i, i + 0x8000))
  return 'data:application/pdf;base64,' + btoa(s)
}

/* ══════════════════════ Host ══════════════════════ */

export function ProgramModelHost() {
  const [launch, setLaunch] = useState(null)
  useEffect(() => {
    function onOpen(e) {
      const conceptId = e.detail?.conceptId
      if (!conceptId) return
      setLaunch({ conceptId, projectId: ctx.recordId || wf.zoomProjectId || null })
    }
    window.addEventListener('open-program-model-modal', onOpen)
    return () => window.removeEventListener('open-program-model-modal', onOpen)
  }, [])
  if (!launch) return null
  return <ProgramModelModal key={launch.conceptId} {...launch} onClose={() => setLaunch(null)} />
}

/* ══════════════════════ Modal ══════════════════════ */

export default function ProgramModelModal({ conceptId, projectId, onClose }) {
  const [vp, setVp] = useState({ x: 0, y: 0, z: 1 })
  const [tool, setTool] = useState('select')          // 'select' | 'scale' | 'draw' | 'anchor' | 'elevation' | 'section'
  const [drawRoomId, setDrawRoomId] = useState(null)  // room armed for polygon placement
  const [selectedRoomId, setSelectedRoomId] = useState(null)
  const [bubble, setBubble] = useState(null)          // { p1, p2, feet:'', inches:'' }
  const [units, setUnits] = useState(null)            // null = loading
  const [levelsByUnit, setLevelsByUnit] = useState({})
  const [activeLevelId, setActiveLevelId] = useState(null)
  const [roomsByLevel, setRoomsByLevel] = useState({})     // { [levelId]: null(in-flight) | Room[] }
  const [plansByLevel, setPlansByLevel] = useState({})
  const [polysByRoom, setPolysByRoom] = useState({})
  const [viewsByUnit, setViewsByUnit] = useState({})  // { [unitId]: view[] } — Unit Views (elevation/section callouts)
  const [addViewUnitId, setAddViewUnitId] = useState(null)  // unit targeted by the Add View picker
  const [viewDraftUnitId, setViewDraftUnitId] = useState(null) // unit armed for view-callout placement
  const [selectedViewId, setSelectedViewId] = useState(null)   // view whose label grip is draggable
  const [pendingArm, setPendingArm] = useState(null)  // { unitId, viewType, editingViewId } | null — armView once activeLevelId catches up to a just-issued switchLevel
  const [scale, setScale] = useState(null)            // active level's { p1, p2, feet, sppf } | null
  const [canvasState, setCanvasState] = useState('empty')  // 'empty' | 'loading' | 'ready' | 'error'
  const [canvasError, setCanvasError] = useState('')
  const [savingScale, setSavingScale] = useState(false)
  const [syncing, setSyncing] = useState(0)           // in-flight Area Manual writes
  const [syncErrors, setSyncErrors] = useState({})    // { [roomId]: true }
  const [selection, setSelection] = useState(null)    // { kind: 'room'|'level'|'unit', id } | null — drives the properties palette
  const [expanded, setExpanded] = useState(() => new Set())  // expanded unit/level ids in the Building tree
  const [addRoomLevelId, setAddRoomLevelId] = useState(null) // level targeted by the Add Room form
  const [sheetPickerOpen, setSheetPickerOpen] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [sendingToSketch, setSendingToSketch] = useState(false)
  const [leftOpen, setLeftOpen] = useState(true)
  const [propW, setPropW] = useState(() => loadPanelW('aj-pm-propw', DEFAULT_PROP_W))
  const [treeW, setTreeW] = useState(() => loadPanelW('aj-pm-treew', DEFAULT_TREE_W))

  const containerRef = useRef(null)
  const compRef = useRef(null)
  const fileInputRef = useRef(null)
  const pageRef = useRef(null)               // active level's pdf.js page
  const baseScaleRef = useRef(1)
  const rasterBaseRef = useRef(null)
  const rasterHiRef = useRef(null)
  const renderTaskRef = useRef(null)
  const rafRef = useRef(null)
  const resharpenRef = useRef(null)
  const dragRef = useRef(null)
  const draftRef = useRef(null)              // { pts: [[fx,fy],...] } in-progress polygon
  const viewDraftRef = useRef(null)          // { pts: [[fx,fy],...] } in-progress view callout (2 pts elevation, 3 pts section)
  const viewLabelAnchorsRef = useRef(new Map())  // { [viewId]: { p:[sx,sy] } } — repopulated every render, screen-space
  const hoverRef = useRef(null)
  const pendingP1Ref = useRef(null)
  const spaceRef = useRef(false)
  const frameRef = useRef(null)              // active level's page frame
  const pdfCacheRef = useRef({})             // { [levelId]: { page, frame } } — level flips don't re-fetch
  const pendingSyncRef = useRef({})          // { [roomId]: timeoutId } debounced polygon saves
  const { resolveUpload, picker } = usePdfPagePicker()

  const st = useRef({})
  st.current = { vp, tool, drawRoomId, selectedRoomId, bubble, scale, activeLevelId, roomsByLevel, plansByLevel, polysByRoom, canvasState, selection, addRoomLevelId, sheetPickerOpen, levelsByUnit, viewsByUnit, viewDraftUnitId, addViewUnitId, selectedViewId }

  // Hoisted here (not just inside PropertiesPanel) so handleAddRoom can seed a new room's
  // derived Cost Factor immediately, instead of leaving it at the flat createRoom default.
  const templates = useRoomTemplates()

  const conceptName = useMemo(() => {
    const rec = (ctx.conceptCache || []).find(c => c.id === conceptId)
    return rec?.fields?.['Parti Pris Title'] || 'Concept'
  }, [conceptId])

  const activePlan = plansByLevel[activeLevelId] || null
  const activeRooms = Array.isArray(roomsByLevel[activeLevelId]) ? roomsByLevel[activeLevelId] : null

  function findLevel(levelId) {
    for (const arr of Object.values(levelsByUnit)) {
      const l = (arr || []).find(x => x.id === levelId)
      if (l) return l
    }
    return null
  }

  /** Add a room to a level — reuses the outline's own RoomFormModal (ConceptRoomsSection.jsx)
   *  so this requires the exact same fields (Room Template, Room Name, Scope Type, optional
   *  Room Number) as adding a room from the outline. */
  async function handleAddRoom(fields) {
    const levelId = addRoomLevelId
    if (!levelId) return
    const level = findLevel(levelId)
    // Cost Factor is an Airtable FORMULA field now (Round 6) — Airtable derives it itself the
    // instant Room Template is set, from a live lookup + a Structural Characteristics formula.
    // Never send it; the API rejects writes to formula fields anyway.
    const created = await createRoom(levelId, conceptId, level?.fields?.['Name'] || '', fields)
    setRoomsByLevel(prev => {
      const list = Array.isArray(prev[levelId]) ? prev[levelId] : []
      return { ...prev, [levelId]: [...list, created] }
    })
  }

  /** Lazy per-level room fetch — at most one Airtable call per level per session. Called from
   *  the active-level effect, tree expansion, and level/unit selection (roll-ups need rooms). */
  function ensureRooms(levelId) {
    if (!levelId || st.current.roomsByLevel[levelId] !== undefined) return
    setRoomsByLevel(prev => prev[levelId] !== undefined ? prev : ({ ...prev, [levelId]: null }))
    getLevelRooms(levelId)
      .then(recs => setRoomsByLevel(prev => ({ ...prev, [levelId]: recs || [] })))
      .catch(() => setRoomsByLevel(prev => ({ ...prev, [levelId]: [] })))
  }

  function toggleExpand(id) {
    setExpanded(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  /** The one selection entry point — tree clicks and canvas polygon clicks both land here.
   *  Selecting a room on another level switches the canvas to that level (keeping the
   *  selection); selecting a level activates it; selecting a unit prefetches every level's
   *  rooms so the roll-up can compute. */
  function selectEntity(kind, id, ctxLevelId) {
    if (kind === 'room') {
      setSelection({ kind: 'room', id })
      setSelectedRoomId(id)
      if (ctxLevelId && ctxLevelId !== st.current.activeLevelId) {
        switchLevel(ctxLevelId, { keepSelection: true })
      } else {
        const p = st.current.polysByRoom[id]
        if (p && p.planId === st.current.plansByLevel[st.current.activeLevelId]?.id) centerPoly(p)
      }
    } else if (kind === 'level') {
      setSelection({ kind: 'level', id })
      setSelectedRoomId(null)
      ensureRooms(id)
      if (id !== st.current.activeLevelId) switchLevel(id, { keepSelection: true })
    } else if (kind === 'unit') {
      setSelection({ kind: 'unit', id })
      setSelectedRoomId(null)
      for (const l of (st.current.levelsByUnit[id] || [])) ensureRooms(l.id)
    }
  }

  /** Persist a room field edit from the properties palette; the server response carries
   *  recomputed formula fields (Room Area Net/Gross, Budget Room Cost Low/High), so the
   *  palette's budget numbers refresh on every save. */
  async function saveRoomFields(roomId, fields) {
    const updated = await updateRoom(roomId, fields)
    setRoomsByLevel(prev => {
      const out = {}
      for (const [lvl, rooms] of Object.entries(prev)) {
        out[lvl] = Array.isArray(rooms)
          ? rooms.map(r => r.id === roomId ? { ...r, fields: updated?.fields || { ...r.fields, ...fields } } : r)
          : rooms
      }
      return out
    })
  }

  /** Roll-up across one level's rooms. null = rooms still loading. */
  function levelRollup(levelId) {
    const rooms = roomsByLevel[levelId]
    if (!Array.isArray(rooms)) return null
    const planId = plansByLevel[levelId]?.id
    const out = { rooms: rooms.length, placed: 0, net: 0, gross: 0, low: 0, high: 0 }
    for (const r of rooms) {
      const f = r.fields || {}
      out.net += Number(f['Room Area Net']) || 0
      out.gross += Number(f['Room Area Gross']) || 0
      out.low += Number(f['Budget Room Cost Low']) || 0
      out.high += Number(f['Budget Room Cost High']) || 0
      const p = polysByRoom[r.id]
      if (p && planId && p.planId === planId) out.placed++
    }
    return out
  }

  /** Roll-up across every level of a unit. loading=true until all levels' rooms are in. */
  function unitRollup(unitId) {
    const levels = levelsByUnit[unitId] || []
    const out = { levels: levels.length, rooms: 0, placed: 0, net: 0, gross: 0, low: 0, high: 0, loading: false }
    for (const l of levels) {
      const r = levelRollup(l.id)
      if (!r) { out.loading = true; continue }
      out.rooms += r.rooms; out.placed += r.placed
      out.net += r.net; out.gross += r.gross
      out.low += r.low; out.high += r.high
    }
    return out
  }

  /** Drag-resize a side panel; the final width persists so the tool reopens the same way. */
  function startPanelResize(side, base, e) {
    e.preventDefault()
    const startX = e.clientX
    let last = base
    const move = mv => {
      last = clamp(base + (mv.clientX - startX) * (side === 'left' ? 1 : -1), PANEL_MIN, PANEL_MAX)
      ;(side === 'left' ? setPropW : setTreeW)(last)
    }
    const up = () => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
      try { localStorage.setItem(side === 'left' ? 'aj-pm-propw' : 'aj-pm-treew', String(Math.round(last))) } catch { /* noop */ }
    }
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
  }

  /* ── Bootstrap: units + levels + the whole concept's plans/polygons ─────── */
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const [unitRecs, model] = await Promise.all([
          getConceptUnits(conceptId),
          getProgramModel(conceptId),
        ])
        if (cancelled) return
        const lv = {}
        for (const u of (unitRecs || [])) {
          lv[u.id] = await getConceptLevels(u.id)
          if (cancelled) return
        }
        setUnits(unitRecs || [])
        setLevelsByUnit(lv)
        setPlansByLevel(model.plansByLevel)
        setPolysByRoom(model.polysByRoom)
        setViewsByUnit(model.viewsByUnit)
        setExpanded(new Set((unitRecs || []).map(u => u.id)))   // units open by default
        const firstLevel = (unitRecs || []).map(u => (lv[u.id] || [])[0]).find(Boolean)
        if (firstLevel) setActiveLevelId(firstLevel.id)
      } catch (e) {
        if (!cancelled) {
          setUnits([])
          await alertDialog({ title: 'Could not load', message: e.message || 'The Building Information Model could not be loaded.' })
        }
      }
    })()
    return () => { cancelled = true }
  }, [conceptId]) // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Active level: rooms (cached per level) + plan PDF ──────────────────── */
  useEffect(() => {
    if (!activeLevelId) return
    ensureRooms(activeLevelId)
    setExpanded(prev => prev.has(activeLevelId) ? prev : new Set(prev).add(activeLevelId))
    loadLevelPdf(activeLevelId)
  }, [activeLevelId, plansByLevel[activeLevelId]?.pdfUrl]) // eslint-disable-line react-hooks/exhaustive-deps

  async function loadLevelPdf(levelId) {
    const plan = st.current.plansByLevel[levelId]
    setScale(scaleFromPlan(plan))
    pageRef.current = null
    rasterBaseRef.current = null
    rasterHiRef.current = null
    frameRef.current = null
    // Selection intentionally NOT cleared here — switchLevel owns that decision (a room
    // selected from the tree on another level must survive the canvas switching to it).
    draftRef.current = null
    pendingP1Ref.current = null
    setBubble(null)
    if (!plan || !plan.pdfUrl) { setCanvasState('empty'); scheduleRender(); return }
    setCanvasState('loading')
    try {
      let cached = pdfCacheRef.current[levelId]
      if (!cached || cached.pdfUrl !== plan.pdfUrl) {
        const pdfjs = await loadPdfjs()
        if (!pdfjs) throw new Error('PDF viewer failed to load. Check your connection and try again.')
        const resp = await fetch(plan.pdfUrl)
        if (!resp.ok) throw new Error('Could not fetch the plan PDF (HTTP ' + resp.status + ').')
        const buf = await resp.arrayBuffer()
        // getDocument() may transfer/detach its input buffer — hand it a copy and keep the
        // original for Send-to-Sketch (see handleSendToSketch), which needs the raw bytes.
        const doc = await pdfjs.getDocument({ data: buf.slice(0) }).promise
        const page = await doc.getPage(1)
        cached = { page, pdfUrl: plan.pdfUrl, bytes: buf }
        pdfCacheRef.current[levelId] = cached
      }
      if (st.current.activeLevelId !== levelId) return   // user flipped away mid-load
      const page = cached.page
      pageRef.current = page
      // Prefer the stored fingerprint (what the geometry was saved against).
      if (isFrame(plan.pageFrame)) frameRef.current = plan.pageFrame
      else {
        const view = page.view
        frameRef.current = { w: view[2] - view[0], h: view[3] - view[1], rotation: normalizeRotation(page.rotate || 0), ox: view[0], oy: view[1] }
      }
      const vp1 = page.getViewport({ scale: 1 })
      const bs = clamp(TARGET_PX / Math.max(vp1.width, vp1.height), 0.5, 4)
      baseScaleRef.current = bs
      const base = await renderRaster(bs)
      if (!base || st.current.activeLevelId !== levelId) return
      rasterBaseRef.current = base
      const cont = containerRef.current
      if (cont) {
        const cw = cont.clientWidth, ch = cont.clientHeight
        const fit = clamp(Math.min((cw - 40) / base.canvas.width, (ch - 40) / base.canvas.height), 0.05, 2)
        setVp({ x: Math.max(20, (cw - base.canvas.width * fit) / 2), y: Math.max(20, (ch - base.canvas.height * fit) / 2), z: fit })
      }
      setCanvasState('ready')
      scheduleRender()
    } catch (e) {
      if (st.current.activeLevelId !== levelId) return
      setCanvasError(e.message || 'Could not open the plan.')
      setCanvasState('error')
    }
  }

  /** Open the active level's background plan, already scaled, as a new layer in a brand-new
   *  /sketch document, opened in a new tab — same handoff ConceptDrawingModal.jsx's "Send to
   *  Sketch" uses: build the v2 doc in-memory, save it as a new sketch, then hand the tab
   *  nothing but that sketch's own id (the new tab loads the rest itself). Renders its own
   *  scale-1 canvas rather than reusing renderRaster()/renderTaskRef, so it can't cancel or
   *  interfere with whatever the live viewport is mid-rendering. */
  async function handleSendToSketch() {
    const levelId = st.current.activeLevelId
    const page = pageRef.current
    const cached = pdfCacheRef.current[levelId]
    if (!page || !cached?.bytes || sendingToSketch) return
    setSendingToSketch(true)
    try {
      const vp1 = page.getViewport({ scale: 1 })
      const c = document.createElement('canvas')
      c.width = Math.ceil(vp1.width); c.height = Math.ceil(vp1.height)
      await page.render({ canvasContext: c.getContext('2d'), viewport: vp1 }).promise
      const level = findLevel(levelId)
      const name = (level?.fields?.['Name'] || 'Level') + ' — ' + conceptName
      const fitZ = clamp(Math.min(1, 1200 / vp1.width, 800 / vp1.height), 0.05, 1)
      const doc = {
        version: 2,
        scalePixPerFoot: st.current.scale?.sppf || null, scaleUnit: 'imperial',
        vp: { x: 40, y: 40, z: fitZ },
        layers: [{
          name, type: 'image', visible: true, x: 0, y: 0, w: vp1.width, h: vp1.height, rotation: 0,
          paperOpacity: 1, inkOpacity: 1, paperColor: 'buff', aspectRatio: vp1.width / vp1.height,
          pdfData: u8ToDataUrl(new Uint8Array(cached.bytes)),
          png: c.toDataURL('image/png'),
        }],
        measurements: [], textObjects: [],
      }
      const jsonBlob = new Blob([JSON.stringify(doc)], { type: 'application/json' })
      const thumbBlob = await new Promise(res => {
        const tc = document.createElement('canvas')
        const tw = 320, th = Math.max(1, Math.round(c.height * (tw / c.width)))
        tc.width = tw; tc.height = th
        tc.getContext('2d').drawImage(c, 0, 0, tw, th)
        tc.toBlob(res, 'image/png')
      })
      const newId = await saveSketch(null, name, jsonBlob, thumbBlob)
      window.open('/sketch?id=' + newId, '_blank', 'noopener')
    } catch (err) {
      await alertDialog({ title: 'Send to AlderSKETCH failed', message: err.message || 'Unknown error.' })
    } finally {
      setSendingToSketch(false)
    }
  }

  // Lock background scroll while open.
  useEffect(() => {
    const b = document.body.style.overflow, h = document.documentElement.style.overflow
    document.body.style.overflow = 'hidden'; document.documentElement.style.overflow = 'hidden'
    return () => { document.body.style.overflow = b; document.documentElement.style.overflow = h }
  }, [])

  useEffect(() => { scheduleRender() })
  useEffect(() => {
    const onResize = () => scheduleRender()
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  // Arms view placement/editing once the target level has actually finished loading — not just
  // once activeLevelId itself updates. A switchLevel call (see handleEditView) triggers an ASYNC
  // loadLevelPdf; canvasState stays 'loading' until that settles, and only flips to 'ready' after
  // scale/anchor/frame are all populated for the NEW level. Waiting on canvasState (rather than
  // re-deriving readiness from plansByLevel/scale directly) sidesteps a real race: st.current is
  // only refreshed at render time, so checking scale readiness the instant activeLevelId changes
  // can still see the PREVIOUS level's cached scale for one render.
  useEffect(() => {
    if (!pendingArm) return
    if (findUnitForLevel(activeLevelId) !== pendingArm.unitId) return
    if (canvasState !== 'ready') return
    const p = pendingArm
    setPendingArm(null)
    armView(p.unitId, p.viewType, p.editingViewId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingArm, activeLevelId, canvasState])

  // Flush debounced polygon saves when the modal unmounts.
  useEffect(() => () => flushPendingSaves(), []) // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Raster helpers (TakeoffEditor pattern) ─────────────────────────────── */
  async function renderRaster(requestScale) {
    const page = pageRef.current
    if (!page) return null
    const vp1 = page.getViewport({ scale: 1 })
    const longEdge = Math.max(vp1.width, vp1.height)
    const area = vp1.width * vp1.height
    const safeScale = Math.max(0.1, Math.min(requestScale, MAX_RASTER_DIM / longEdge, Math.sqrt(MAX_RASTER_AREA / area)))
    const viewport = page.getViewport({ scale: safeScale })
    const c = document.createElement('canvas')
    c.width = Math.ceil(viewport.width); c.height = Math.ceil(viewport.height)
    if (renderTaskRef.current) { try { renderTaskRef.current.cancel() } catch { /* ignore */ } }
    const task = page.render({ canvasContext: c.getContext('2d'), viewport })
    renderTaskRef.current = task
    try { await task.promise } catch (e) { if (e?.name === 'RenderingCancelledException') return null; throw e }
    return { canvas: c }
  }

  async function resharpen() {
    if (!pageRef.current) return
    const z = st.current.vp.z
    const hi = await renderRaster(baseScaleRef.current * z)
    if (!hi) return
    rasterHiRef.current = { canvas: hi.canvas, z }
    scheduleRender()
  }

  /* ── Coordinate chain (screen ↔ content ↔ visual fraction) ──────────────── */
  function baseDims() { const b = rasterBaseRef.current; return b ? { bw: b.canvas.width, bh: b.canvas.height } : null }
  function evtScreen(e) { const r = compRef.current.getBoundingClientRect(); return [e.clientX - r.left, e.clientY - r.top] }
  function screenToContent(sx, sy) { const v = st.current.vp; return [(sx - v.x) / v.z, (sy - v.y) / v.z] }
  function contentToScreen(cx, cy) { const v = st.current.vp; return [cx * v.z + v.x, cy * v.z + v.y] }
  function contentToFrac(cx, cy) { const d = baseDims(); return d ? [cx / d.bw, cy / d.bh] : [0, 0] }
  function fracToContent(f) { const d = baseDims(); return d ? [f[0] * d.bw, f[1] * d.bh] : [0, 0] }
  function fracToScreen(f) { const c = fracToContent(f); return contentToScreen(c[0], c[1]) }
  function evtFrac(e) { const [sx, sy] = evtScreen(e); const [cx, cy] = screenToContent(sx, sy); return contentToFrac(cx, cy) }
  function pxPerPoint() {
    const d = baseDims(), fr = frameRef.current
    if (!d || !fr) return 1
    return d.bw / visualSize(fr).vw
  }

  /* ── Unit Views: anchor-relative feet ↔ frac (see docs/PROGRAM_MODEL.md — "Unit Views") ───
     A View's callout is stored as FEET offset from its Unit's per-level alignment anchor —
     never a raw page fraction — so the SAME callout data projects correctly onto whichever
     level is currently displayed: convert using THAT level's own anchor + scale (both already
     loaded into frameRef/scale/plansByLevel[activeLevelId] whenever a level is active), never
     the level the callout originated on. No inter-level rotation solve in v1 (see the doc). */
  function contentDeltaToFeet(dx, dy) {
    const sppf = st.current.scale?.sppf, ppp = pxPerPoint()
    if (!(sppf > 0) || !(ppp > 0)) return null
    return { x: dx / ppp / sppf, y: dy / ppp / sppf }
  }
  function feetDeltaToContent(fx, fy) {
    const sppf = st.current.scale?.sppf, ppp = pxPerPoint()
    if (!(sppf > 0) || !(ppp > 0)) return null
    return { x: fx * sppf * ppp, y: fy * sppf * ppp }
  }
  /** A clicked frac point on the ACTIVE level -> feet offset from that level's own anchor. */
  function fracToAnchorFeet(frac) {
    const anchor = st.current.plansByLevel[st.current.activeLevelId]?.anchorPoint
    if (!anchor) return null
    const ac = fracToContent(anchor), pc = fracToContent(frac)
    return contentDeltaToFeet(pc[0] - ac[0], pc[1] - ac[1])
  }
  /** A view's feet offset -> a frac point on the ACTIVE level (for rendering there). */
  function viewPointToFrac(feet) {
    const anchor = st.current.plansByLevel[st.current.activeLevelId]?.anchorPoint
    if (!anchor) return null
    const d = feetDeltaToContent(feet.x, feet.y)
    if (!d) return null
    const ac = fracToContent(anchor)
    return contentToFrac(ac[0] + d.x, ac[1] + d.y)
  }
  function findUnitForLevel(levelId) {
    for (const [unitId, levels] of Object.entries(st.current.levelsByUnit)) {
      if ((levels || []).some(l => l.id === levelId)) return unitId
    }
    return null
  }

  /* ── Composite render ───────────────────────────────────────────────────── */
  function scheduleRender() { if (rafRef.current) return; rafRef.current = requestAnimationFrame(() => { rafRef.current = null; renderAll() }) }

  function renderAll() {
    const comp = compRef.current, cont = containerRef.current, base = rasterBaseRef.current
    if (!comp || !cont) return
    const dpr = window.devicePixelRatio || 1
    const cw = cont.clientWidth, ch = cont.clientHeight
    if (comp.width !== Math.floor(cw * dpr) || comp.height !== Math.floor(ch * dpr)) {
      comp.width = Math.floor(cw * dpr); comp.height = Math.floor(ch * dpr)
      comp.style.width = cw + 'px'; comp.style.height = ch + 'px'
    }
    const g = comp.getContext('2d')
    g.setTransform(dpr, 0, 0, dpr, 0, 0)
    g.clearRect(0, 0, cw, ch)
    if (!base) return
    const v = st.current.vp
    const baseW = base.canvas.width, baseH = base.canvas.height
    g.save(); g.translate(v.x, v.y); g.scale(v.z, v.z)
    const hi = rasterHiRef.current
    if (hi && Math.abs(hi.z - v.z) < 0.02) g.drawImage(hi.canvas, 0, 0, baseW, baseH)
    else g.drawImage(base.canvas, 0, 0, baseW, baseH)
    g.restore()
    // Room polygons for the active level (selected last, on top)
    const rooms = Array.isArray(st.current.roomsByLevel[st.current.activeLevelId]) ? st.current.roomsByLevel[st.current.activeLevelId] : []
    const roomIds = new Set(rooms.map(r => r.id))
    const activePlanId = st.current.plansByLevel[st.current.activeLevelId]?.id
    const polys = Object.values(st.current.polysByRoom).filter(p => p.planId === activePlanId)
    for (const p of polys) {
      if (p.roomId !== st.current.selectedRoomId) drawRoomPoly(g, p, rooms, false, !roomIds.has(p.roomId))
    }
    const sel = polys.find(p => p.roomId === st.current.selectedRoomId)
    if (sel) drawRoomPoly(g, sel, rooms, true, !roomIds.has(sel.roomId))
    drawDraft(g)
    drawCalibration(g)
    drawAnchor(g)
    drawViews(g)
    drawViewDraft(g)
    drawScaleBar(g, cw, ch)
  }

  function drawLabel(g, text, x, y, color) {
    g.save()
    g.font = '600 11px "DM Mono", monospace'
    g.textAlign = 'center'; g.textBaseline = 'middle'
    g.lineWidth = 3.5; g.strokeStyle = 'rgba(255,255,255,0.92)'; g.lineJoin = 'round'
    g.strokeText(text, x, y)
    g.fillStyle = color || '#1f2937'
    g.fillText(text, x, y)
    g.restore()
  }

  function drawRoomPoly(g, poly, rooms, selected, orphan) {
    const pts = Array.isArray(poly.points) ? poly.points : []
    if (pts.length < 3) return
    const room = rooms.find(r => r.id === poly.roomId)
    const col = orphan ? '#9ca3af' : roomColor(poly.roomId)
    const S = pts.map(p => fracToScreen(p))
    g.save()
    if (orphan) g.setLineDash([4, 4])
    g.beginPath(); g.moveTo(S[0][0], S[0][1])
    for (let i = 1; i < S.length; i++) g.lineTo(S[i][0], S[i][1])
    g.closePath()
    g.fillStyle = withAlpha(col, selected ? 0.3 : 0.18); g.fill()
    g.strokeStyle = selected ? SELECT_COLOR : col; g.lineWidth = selected ? 2.5 : 1.6; g.stroke()
    g.setLineDash([])
    const c = polygonCentroid(S)
    const name = orphan ? 'room deleted' : (room ? roomDisplayName(room) : '')
    const sf = poly.computedSf != null ? fmtSF(poly.computedSf) : ''
    drawLabel(g, name + (sf ? ' · ' + sf : ''), c[0], c[1], orphan ? '#6b7280' : col)
    if (selected) {
      g.fillStyle = '#fff'; g.strokeStyle = SELECT_COLOR; g.lineWidth = 1.5
      for (const p of S) { g.fillRect(p[0] - HANDLE / 2, p[1] - HANDLE / 2, HANDLE, HANDLE); g.strokeRect(p[0] - HANDLE / 2, p[1] - HANDLE / 2, HANDLE, HANDLE) }
    }
    g.restore()
  }

  function drawDraft(g) {
    const d = draftRef.current
    if (!d || !st.current.drawRoomId) return
    const col = roomColor(st.current.drawRoomId)
    const S = d.pts.map(p => fracToScreen(p))
    const hov = hoverRef.current ? fracToScreen(hoverRef.current) : null
    const all = hov ? S.concat([hov]) : S
    if (!all.length) return
    g.save()
    g.beginPath(); g.moveTo(all[0][0], all[0][1])
    for (let i = 1; i < all.length; i++) g.lineTo(all[i][0], all[i][1])
    if (all.length >= 3) { g.closePath(); g.fillStyle = withAlpha(col, 0.14); g.fill() }
    g.strokeStyle = col; g.lineWidth = 1.8; g.setLineDash([5, 3]); g.stroke(); g.setLineDash([])
    S.forEach((p, i) => {
      g.beginPath(); g.arc(p[0], p[1], i === 0 ? 5 : 3.5, 0, 2 * Math.PI)
      g.fillStyle = i === 0 ? col : '#fff'; g.fill()
      g.strokeStyle = col; g.lineWidth = 1.3; g.stroke()
    })
    if (hov && all.length >= 3 && scaleReady()) {
      const fracPts = d.pts.concat([hoverRef.current])
      const val = computeRegionValue('area', fracPts, frameRef.current, st.current.scale.sppf)
      if (val != null) drawLabel(g, fmtSF(val), hov[0] + 14, hov[1] - 12, col)
    }
    g.restore()
  }

  function calHandles() {
    const s = st.current.scale
    const out = []
    if (s) { out.push({ which: 'p1', frac: s.p1 }, { which: 'p2', frac: s.p2 }) }
    else if (pendingP1Ref.current) out.push({ which: 'pending', frac: pendingP1Ref.current })
    return out
  }

  function drawCalibration(g) {
    const s = st.current.scale
    const b = st.current.bubble
    const pending = pendingP1Ref.current
    const pts = b ? [b.p1, b.p2] : (pending ? [pending] : (s && st.current.tool === 'scale' ? [s.p1, s.p2] : []))
    if (!pts.length) return
    const S = pts.map(p => fracToScreen(p))
    g.save()
    let lineTo = null
    if (S.length === 2) lineTo = S[1]
    else if (st.current.tool === 'scale' && hoverRef.current) lineTo = fracToScreen(hoverRef.current)
    if (lineTo) {
      g.beginPath(); g.moveTo(S[0][0], S[0][1]); g.lineTo(lineTo[0], lineTo[1])
      g.strokeStyle = CAL_COLOR; g.lineWidth = 1.6; g.setLineDash([6, 4]); g.stroke(); g.setLineDash([])
    }
    for (const p of S) {
      g.beginPath(); g.arc(p[0], p[1], 7, 0, 2 * Math.PI)
      g.fillStyle = 'rgba(255,255,255,0.9)'; g.fill()
      g.lineWidth = 2; g.strokeStyle = CAL_COLOR; g.stroke()
      g.beginPath(); g.moveTo(p[0] - 3.5, p[1]); g.lineTo(p[0] + 3.5, p[1]); g.moveTo(p[0], p[1] - 3.5); g.lineTo(p[0], p[1] + 3.5)
      g.lineWidth = 1.4; g.stroke()
    }
    if (!b && !pending && s && S.length === 2) {
      drawLabel(g, feetLabel(s.feet), (S[0][0] + S[1][0]) / 2, (S[0][1] + S[1][1]) / 2 - 14, CAL_COLOR)
    }
    g.restore()
  }

  function drawScaleBar(g, cw, ch) {
    const s = st.current.scale
    if (!s || !(s.sppf > 0)) return
    const pxPerFt = s.sppf * pxPerPoint() * st.current.vp.z
    if (!(pxPerFt > 0)) return
    let barFt = BAR_CANDIDATES[0]
    for (const c of BAR_CANDIDATES) { if (c * pxPerFt <= 180) barFt = c; else break }
    const barPx = barFt * pxPerFt
    if (barPx < 24) return
    const bx = 16, by = ch - 20
    g.save()
    g.fillStyle = 'rgba(255,255,255,0.85)'
    g.fillRect(bx - 8, by - 16, barPx + 58, 28)
    g.strokeStyle = DARK; g.lineWidth = 2
    g.beginPath(); g.moveTo(bx, by); g.lineTo(bx + barPx, by); g.stroke()
    g.beginPath(); g.moveTo(bx, by - 5); g.lineTo(bx, by + 5); g.stroke()
    g.beginPath(); g.moveTo(bx + barPx, by - 5); g.lineTo(bx + barPx, by + 5); g.stroke()
    g.fillStyle = DARK; g.font = '600 10px "DM Mono", monospace'; g.textAlign = 'left'; g.textBaseline = 'middle'
    g.fillText(barFt + "'", bx + barPx + 8, by - 1)
    g.restore()
  }

  /* ── Unit Views: anchor marker + callout rendering ──────────────────────── */
  function drawAnchor(g) {
    const anchor = st.current.plansByLevel[st.current.activeLevelId]?.anchorPoint
    if (!anchor) return
    const p = fracToScreen(anchor)
    g.save()
    g.strokeStyle = ANCHOR_COLOR; g.lineWidth = 1.6
    g.beginPath(); g.arc(p[0], p[1], 6, 0, 2 * Math.PI); g.stroke()
    g.beginPath(); g.moveTo(p[0] - 10, p[1]); g.lineTo(p[0] + 10, p[1]); g.moveTo(p[0], p[1] - 10); g.lineTo(p[0], p[1] + 10)
    g.stroke()
    g.restore()
  }

  function drawArrowHead(g, from, to, size, color) {
    const a = Math.atan2(to[1] - from[1], to[0] - from[0])
    g.save(); g.translate(to[0], to[1]); g.rotate(a)
    g.beginPath(); g.moveTo(0, 0); g.lineTo(-size, -size * 0.45); g.lineTo(-size, size * 0.45); g.closePath()
    g.fillStyle = color; g.fill()
    g.restore()
  }

  /** In-progress view-callout placement (2 clicks for elevation, 3 for section) — mirrors
   *  drawDraft()'s live-preview pattern for room polygons. */
  function drawViewDraft(g) {
    const t = st.current.tool
    const d = viewDraftRef.current
    if (!d || (t !== 'elevation' && t !== 'section')) return
    const S = d.pts.map(p => fracToScreen(p))
    const hov = hoverRef.current ? fracToScreen(hoverRef.current) : null
    const all = hov ? S.concat([hov]) : S
    if (!all.length) return
    g.save()
    g.strokeStyle = VIEW_COLOR; g.lineWidth = 1.8; g.setLineDash([5, 3])
    g.beginPath(); g.moveTo(all[0][0], all[0][1])
    for (let i = 1; i < all.length; i++) g.lineTo(all[i][0], all[i][1])
    g.stroke(); g.setLineDash([])
    S.forEach((p, i) => {
      g.beginPath(); g.arc(p[0], p[1], i === 0 ? 5 : 3.5, 0, 2 * Math.PI)
      g.fillStyle = '#fff'; g.fill()
      g.strokeStyle = VIEW_COLOR; g.lineWidth = 1.3; g.stroke()
    })
    g.restore()
  }

  /** Resolves a view's label draw position: its own default anchor (baseFeet, next to the
   *  marker) plus whatever manual offset the user dragged it to (callout.labelDx/labelDy —
   *  persisted, FEET, mirrors SiteAnalysisModal.jsx's labelOffset pattern exactly). Stashes the
   *  result in viewLabelAnchorsRef (screen-space, repopulated every render) so the selection
   *  overlay can draw a grip there and onPointerDown can hit-test it. No leader line is drawn back
   *  to baseFeet — the label floats free of the marker once dragged. */
  function viewLabelAnchor(v, baseFeet) {
    const c = v.callout
    const feet = { x: baseFeet.x + (c.labelDx || 0), y: baseFeet.y + (c.labelDy != null ? c.labelDy : DEFAULT_LABEL_FEET_DY) }
    const frac = viewPointToFrac(feet)
    if (!frac) return null
    const anchor = { p: fracToScreen(frac) }
    viewLabelAnchorsRef.current.set(v.id, anchor)
    return anchor
  }

  function drawViewLabel(g, v, la) {
    if (!la) return
    drawLabel(g, v.name, la.p[0], la.p[1], VIEW_COLOR)
    if (st.current.selectedViewId === v.id) {
      g.save()
      g.beginPath()
      g.moveTo(la.p[0], la.p[1] - 6); g.lineTo(la.p[0] + 6, la.p[1]); g.lineTo(la.p[0], la.p[1] + 6); g.lineTo(la.p[0] - 6, la.p[1])
      g.closePath()
      g.fillStyle = '#fff'; g.fill(); g.strokeStyle = VIEW_COLOR; g.lineWidth = 1; g.stroke()
      g.restore()
    }
  }

  function drawOneView(g, v) {
    const c = v.callout
    if (v.viewType === 'elevation') {
      const pos = viewPointToFrac({ x: c.dx, y: c.dy })
      const dir = viewPointToFrac({ x: c.dirDx, y: c.dirDy })
      if (!pos || !dir) return
      const P = fracToScreen(pos), D = fracToScreen(dir)
      const ang = Math.atan2(D[1] - P[1], D[0] - P[0])
      const tip = [P[0] + Math.cos(ang) * 18, P[1] + Math.sin(ang) * 18]
      g.save()
      g.beginPath(); g.moveTo(P[0], P[1]); g.lineTo(tip[0], tip[1])
      g.strokeStyle = VIEW_COLOR; g.lineWidth = 1.6; g.stroke()
      drawArrowHead(g, P, tip, 9, VIEW_COLOR)
      g.beginPath(); g.arc(P[0], P[1], 8, 0, 2 * Math.PI)
      g.fillStyle = 'rgba(255,255,255,0.92)'; g.fill()
      g.lineWidth = 1.8; g.strokeStyle = VIEW_COLOR; g.stroke()
      g.restore()
      drawViewLabel(g, v, viewLabelAnchor(v, { x: c.dx, y: c.dy }))
    } else {
      const p1 = viewPointToFrac({ x: c.x1, y: c.y1 })
      const p2 = viewPointToFrac({ x: c.x2, y: c.y2 })
      const side = viewPointToFrac({ x: c.sideDx, y: c.sideDy })
      if (!p1 || !p2 || !side) return
      const S1 = fracToScreen(p1), S2 = fracToScreen(p2), Side = fracToScreen(side)
      const mid = [(S1[0] + S2[0]) / 2, (S1[1] + S2[1]) / 2]
      // The end ticks are the ONLY orientation indicator (no separate midpoint arrow) — each
      // is one-directional, bent toward the "looking toward" side, matching the standard
      // architectural section-cut convention. `perp` is one of the two perpendiculars to the
      // cut line; flip it if it doesn't already point toward `side`.
      const lineAng = Math.atan2(S2[1] - S1[1], S2[0] - S1[0])
      let perp = lineAng + Math.PI / 2
      const toSide = [Side[0] - mid[0], Side[1] - mid[1]]
      if (toSide[0] * Math.cos(perp) + toSide[1] * Math.sin(perp) < 0) perp += Math.PI
      g.save()
      g.strokeStyle = VIEW_COLOR; g.lineWidth = 2
      g.beginPath(); g.moveTo(S1[0], S1[1]); g.lineTo(S2[0], S2[1]); g.stroke()
      const tick = 9
      for (const P of [S1, S2]) {
        g.beginPath(); g.moveTo(P[0], P[1])
        g.lineTo(P[0] + Math.cos(perp) * tick, P[1] + Math.sin(perp) * tick)
        g.stroke()
      }
      g.restore()
      drawViewLabel(g, v, viewLabelAnchor(v, { x: (c.x1 + c.x2) / 2, y: (c.y1 + c.y2) / 2 }))
    }
  }

  /** Every View belonging to the active level's Unit, projected onto whatever level is
   *  currently displayed — skipped entirely if that level has no alignment anchor set. */
  function drawViews(g) {
    viewLabelAnchorsRef.current.clear()
    const plan = st.current.plansByLevel[st.current.activeLevelId]
    if (!plan?.anchorPoint || !(st.current.scale?.sppf > 0)) return
    const unitId = findUnitForLevel(st.current.activeLevelId)
    if (!unitId) return
    for (const v of (st.current.viewsByUnit[unitId] || [])) drawOneView(g, v)
  }

  /** The selected view's label-grip hit-test — takes priority over the marker hit-test below
   *  (mirrors SiteAnalysisModal's label-grip-before-item-hit-test ordering). */
  function hitViewLabelGrip(sx, sy) {
    const id = st.current.selectedViewId
    if (!id) return null
    const la = viewLabelAnchorsRef.current.get(id)
    if (!la) return null
    return Math.hypot(sx - la.p[0], sy - la.p[1]) < 8 ? id : null
  }

  /** Hit-test a view's MARKER (not its label) — elevation: near the circle; section: near the
   *  cut line. Only considers views for the active level's own unit (same set drawViews draws). */
  function hitViewMarker(sx, sy) {
    const unitId = findUnitForLevel(st.current.activeLevelId)
    if (!unitId) return null
    const views = st.current.viewsByUnit[unitId] || []
    for (let i = views.length - 1; i >= 0; i--) {
      const v = views[i], c = v.callout
      if (v.viewType === 'elevation') {
        const pos = viewPointToFrac({ x: c.dx, y: c.dy })
        if (!pos) continue
        const P = fracToScreen(pos)
        if (Math.hypot(sx - P[0], sy - P[1]) < 10) return v.id
      } else {
        const p1 = viewPointToFrac({ x: c.x1, y: c.y1 }), p2 = viewPointToFrac({ x: c.x2, y: c.y2 })
        if (!p1 || !p2) continue
        const S1 = fracToScreen(p1), S2 = fracToScreen(p2)
        if (distToSeg([sx, sy], S1, S2) <= 6) return v.id
      }
    }
    return null
  }

  /* ── Hit testing ────────────────────────────────────────────────────────── */
  function hitCalHandle(sx, sy) {
    for (const h of calHandles()) {
      const p = fracToScreen(h.frac)
      if (Math.hypot(sx - p[0], sy - p[1]) <= 10) return h
    }
    return null
  }
  function selectedPoly() {
    const roomId = st.current.selectedRoomId
    if (!roomId) return null
    const p = st.current.polysByRoom[roomId]
    return p && p.planId === st.current.plansByLevel[st.current.activeLevelId]?.id ? p : null
  }
  function hitVertexHandle(sx, sy) {
    const sel = selectedPoly()
    if (!sel) return null
    for (let i = 0; i < sel.points.length; i++) {
      const p = fracToScreen(sel.points[i])
      if (Math.abs(sx - p[0]) <= HANDLE + 3 && Math.abs(sy - p[1]) <= HANDLE + 3) return { roomId: sel.roomId, idx: i }
    }
    return null
  }
  function hitSegment(sx, sy) {
    const sel = selectedPoly()
    if (!sel) return null
    const S = sel.points.map(p => fracToScreen(p))
    for (let j = 0; j < S.length; j++) {
      if (distToSeg([sx, sy], S[j], S[(j + 1) % S.length]) <= 6) return { roomId: sel.roomId, after: j }
    }
    return null
  }
  function hitPoly(sx, sy) {
    const activePlanId = st.current.plansByLevel[st.current.activeLevelId]?.id
    const polys = Object.values(st.current.polysByRoom).filter(p => p.planId === activePlanId)
    for (let i = polys.length - 1; i >= 0; i--) {
      const p = polys[i]
      const S = (p.points || []).map(f => fracToScreen(f))
      if (S.length >= 3) {
        if (pointInPolygon([sx, sy], S)) return p
        for (let j = 0; j < S.length; j++) if (distToSeg([sx, sy], S[j], S[(j + 1) % S.length]) <= 6) return p
      }
    }
    return null
  }

  /* ── Scale ──────────────────────────────────────────────────────────────── */
  function scaleReady() { return !!(st.current.scale && st.current.scale.sppf > 0) }

  async function persistScale(p1, p2, feet) {
    const plan = st.current.plansByLevel[st.current.activeLevelId]
    if (!plan) return null
    const fr = frameRef.current
    const sppf = scalePointsPerFoot(p1, p2, fr, feet)
    if (!(sppf > 0)) return null
    const sp = { p1: roundPt(p1), p2: roundPt(p2), feet }
    // Fractions are scale-independent — recompute every polygon SF on this plan.
    const activePolys = Object.values(st.current.polysByRoom).filter(g => g.planId === plan.id)
    const areas = activePolys.map(g => ({
      roomId: g.roomId,
      computedSf: Math.round(computeRegionValue('area', g.points, fr, sppf) || 0),
    }))
    setSavingScale(true)
    try {
      const updated = await setPlanScale({ planId: plan.id, scalePoints: sp, scalePointsPerFoot: sppf, areas })
      const next = { p1: sp.p1, p2: sp.p2, feet, sppf }
      setScale(next)
      setPlansByLevel(prev => ({ ...prev, [plan.levelId]: updated }))
      if (areas.length) {
        setPolysByRoom(prev => {
          const out = { ...prev }
          for (const a of areas) { if (out[a.roomId]) out[a.roomId] = { ...out[a.roomId], computedSf: a.computedSf } }
          return out
        })
        syncAreasToAirtable(areas)
      }
      return next
    } catch (e) {
      await alertDialog({ title: 'Scale not saved', message: e.message || 'The scale could not be saved. Try again.' })
      return null
    } finally { setSavingScale(false) }
  }

  async function applyBubble() {
    const b = st.current.bubble
    if (!b) return
    const feet = (parseFloat(b.feet) || 0) + (parseFloat(b.inches) || 0) / 12
    if (!(feet > 0)) return
    const ok = await persistScale(b.p1, b.p2, feet)
    if (ok) { setBubble(null); pendingP1Ref.current = null; setTool('select') }
  }

  function cancelBubble() {
    setBubble(null)
    pendingP1Ref.current = null
    scheduleRender()
  }

  /** Single-click alignment anchor — the one shared reference point per level a View's
   *  feet-offset callout is projected against on every level of the unit. Simpler than
   *  Calibrate (one point, no distance entry): click, persist, done. */
  async function persistAnchor(frac) {
    const plan = st.current.plansByLevel[st.current.activeLevelId]
    if (!plan) return
    const pt = roundPt(frac)
    const prior = plan
    setPlansByLevel(p => ({ ...p, [plan.levelId]: { ...p[plan.levelId], anchorPoint: pt } }))
    setTool('select')
    try {
      const updated = await setPlanAnchor({ planId: plan.id, anchorPoint: pt })
      setPlansByLevel(p => ({ ...p, [plan.levelId]: updated }))
      scheduleRender()
    } catch (e) {
      setPlansByLevel(p => ({ ...p, [plan.levelId]: prior }))
      await alertDialog({ title: 'Anchor not saved', message: e.message || 'The alignment anchor could not be saved. Try again.' })
    }
  }

  /** Structural finish elevation (feet, may be negative) — explicitly NOT the finished-floor
   *  elevation (the flooring applied over the structural finish). Plain data-entry field. */
  async function handleSetLevelElevation(planId, levelId, elevation) {
    const prior = plansByLevel[levelId]
    setPlansByLevel(p => ({ ...p, [levelId]: { ...p[levelId], structuralFinishElevation: elevation } }))
    try {
      const updated = await setLevelElevation({ planId, elevation })
      setPlansByLevel(p => ({ ...p, [levelId]: updated }))
    } catch (e) {
      setPlansByLevel(p => ({ ...p, [levelId]: prior }))
      await alertDialog({ title: 'Elevation not saved', message: e.message || 'The elevation could not be saved. Try again.' })
    }
  }

  /* ── Area Manual sync (the derived Airtable side effect) ────────────────── */
  function writeAreaManual(roomId, sf) {
    setSyncing(n => n + 1)
    setSyncErrors(prev => { if (!prev[roomId]) return prev; const out = { ...prev }; delete out[roomId]; return out })
    updateRoom(roomId, { 'Area Manual': sf })
      .then(updated => {
        // Keep the local room record fresh so Room Area Net displays correctly.
        setRoomsByLevel(prev => {
          const out = {}
          for (const [lvl, rooms] of Object.entries(prev)) {
            out[lvl] = Array.isArray(rooms)
              ? rooms.map(r => r.id === roomId ? { ...r, fields: updated?.fields || { ...r.fields, 'Area Manual': sf } } : r)
              : rooms
          }
          return out
        })
      })
      .catch(() => setSyncErrors(prev => ({ ...prev, [roomId]: true })))
      .finally(() => setSyncing(n => n - 1))
  }

  function syncAreasToAirtable(areas) {
    // Sequential through the service queue (429-safe); each write independent.
    for (const a of areas) writeAreaManual(a.roomId, a.computedSf)
  }

  function retrySync(roomId) {
    const poly = st.current.polysByRoom[roomId]
    if (poly && poly.computedSf != null) writeAreaManual(roomId, Math.round(poly.computedSf))
  }

  // Self-heal a background Area Manual sync that failed silently in a prior session: a failed
  // writeAreaManual only ever surfaced as a transient "sync error" badge (syncErrors is plain
  // React state, never persisted), so once the tool was closed there was no lasting sign a room's
  // polygon SF never made it into Airtable — confirmed 2026-07 on a real project where a rapid
  // back-to-back drawing session hit Airtable's rate limit and 9 of 15 rooms silently never
  // synced. Whenever a level's rooms load, compare every room that already has a placed polygon
  // against its current Area Manual and resync any drift — at most once per room per tool session
  // (reconciledRef), so a room that keeps genuinely failing doesn't get hammered on every render.
  const reconciledRef = useRef(new Set())
  useEffect(() => {
    for (const rooms of Object.values(roomsByLevel)) {
      if (!Array.isArray(rooms)) continue
      for (const room of rooms) {
        if (reconciledRef.current.has(room.id)) continue
        const poly = polysByRoom[room.id]
        if (!poly || poly.computedSf == null) continue
        const current = room.fields?.['Area Manual']
        if (Number(current) === Number(poly.computedSf)) continue
        reconciledRef.current.add(room.id)
        writeAreaManual(room.id, Math.round(poly.computedSf))
      }
    }
  }, [roomsByLevel, polysByRoom])

  /* ── Polygon CRUD ───────────────────────────────────────────────────────── */
  function commitDraft() {
    const d = draftRef.current, roomId = st.current.drawRoomId
    draftRef.current = null
    if (!d || !roomId) { scheduleRender(); return }
    const pts = []
    for (const p of d.pts) {
      const last = pts[pts.length - 1]
      if (last) {
        const a = fracToScreen(last), b = fracToScreen(p)
        if (Math.hypot(a[0] - b[0], a[1] - b[1]) < 3) continue
      }
      pts.push(roundPt(p))
    }
    if (pts.length < 3) { scheduleRender(); return }
    const plan = st.current.plansByLevel[st.current.activeLevelId]
    if (!plan) { scheduleRender(); return }
    const sf = Math.round(computeRegionValue('area', pts, frameRef.current, st.current.scale?.sppf) || 0)
    const optimistic = { id: 'tmp_' + roomId, planId: plan.id, roomId, points: pts, computedSf: sf }
    setPolysByRoom(prev => ({ ...prev, [roomId]: optimistic }))
    setTool('select'); setDrawRoomId(null); setSelectedRoomId(roomId)
    saveRoomPolygon({ planId: plan.id, roomId, points: pts, computedSf: sf })
      .then(row => {
        setPolysByRoom(prev => ({ ...prev, [roomId]: row }))
        writeAreaManual(roomId, sf)
      })
      .catch(async e => {
        setPolysByRoom(prev => { const out = { ...prev }; delete out[roomId]; return out })
        await alertDialog({ title: 'Room shape not saved', message: e.message || 'The room shape could not be saved. Try again.' })
      })
    scheduleRender()
  }

  function cancelDraft() { draftRef.current = null; scheduleRender() }

  /* ── Unit Views: callout placement ──────────────────────────────────────── */
  /** Arm the canvas for a new view's callout: 2 clicks for elevation (position, then a point
   *  setting the arrow direction), 3 for section (line start, line end, then a point on the
   *  "looking toward" side). Requires the active level to have its alignment anchor set. */
  /** Arms the click-sequence placement UI. `editingViewId`, when set, means this is an in-place
   *  edit of an EXISTING view (re-placing its geometry — move AND reorient in one gesture) rather
   *  than creating a new one; commitViewDraft branches on it. */
  function armView(unitId, viewType, editingViewId) {
    if (st.current.canvasState !== 'ready' || !scaleReady()) return
    // Belt-and-suspenders: callers (onAddView, the pendingArm effect for edits) already switch to
    // one of this unit's own levels before arming — if that somehow didn't happen, refuse rather
    // than silently placing the callout against the wrong unit/level pairing.
    if (findUnitForLevel(st.current.activeLevelId) !== unitId) return
    if (!st.current.plansByLevel[st.current.activeLevelId]?.anchorPoint) return
    setDrawRoomId(null); cancelDraft(); setSelectedRoomId(null)
    pendingP1Ref.current = null
    viewDraftRef.current = { pts: [], editingViewId: editingViewId || null }
    setViewDraftUnitId(unitId)
    setTool(viewType)
  }

  function cancelViewDraft() { viewDraftRef.current = null; setViewDraftUnitId(null); scheduleRender() }

  async function commitViewDraft(viewType) {
    const d = viewDraftRef.current
    const unitId = st.current.viewDraftUnitId
    const levelId = st.current.activeLevelId
    const editingViewId = d?.editingViewId || null
    viewDraftRef.current = null
    setTool('select'); setViewDraftUnitId(null)
    if (!d || !unitId) { scheduleRender(); return }
    let callout = null
    if (viewType === 'elevation' && d.pts.length >= 2) {
      const pos = fracToAnchorFeet(d.pts[0]), dir = fracToAnchorFeet(d.pts[1])
      if (pos && dir) callout = { dx: pos.x, dy: pos.y, dirDx: dir.x, dirDy: dir.y }
    } else if (viewType === 'section' && d.pts.length >= 3) {
      const p1 = fracToAnchorFeet(d.pts[0]), p2 = fracToAnchorFeet(d.pts[1]), side = fracToAnchorFeet(d.pts[2])
      if (p1 && p2 && side) callout = { x1: p1.x, y1: p1.y, x2: p2.x, y2: p2.y, sideDx: side.x, sideDy: side.y }
    }
    scheduleRender()
    if (!callout) return
    if (editingViewId) {
      // Re-placing geometry only — preserve whatever label offset the user already dragged it to.
      const existing = (st.current.viewsByUnit[unitId] || []).find(v => v.id === editingViewId)
      if (existing?.callout) {
        if (existing.callout.labelDx != null) callout.labelDx = existing.callout.labelDx
        if (existing.callout.labelDy != null) callout.labelDy = existing.callout.labelDy
      }
      try {
        const view = await updateViewCallout(editingViewId, callout)
        setViewsByUnit(prev => ({ ...prev, [unitId]: (prev[unitId] || []).map(v => v.id === editingViewId ? view : v) }))
      } catch (e) {
        await alertDialog({ title: 'View not saved', message: e.message || 'The new position could not be saved. Try again.' })
      }
      return
    }
    const existingCount = (st.current.viewsByUnit[unitId] || []).filter(v => v.viewType === viewType).length
    const name = (viewType === 'elevation' ? 'Elevation ' : 'Section ') + (existingCount + 1)
    try {
      const view = await createUnitView({ unitId, conceptId, projectId, viewType, name, originLevelId: levelId, callout })
      setViewsByUnit(prev => ({ ...prev, [unitId]: [...(prev[unitId] || []), view] }))
    } catch (e) {
      await alertDialog({ title: 'View not saved', message: e.message || 'The view could not be saved. Try again.' })
    }
  }

  /** Debounced persist after a vertex edit — Supabase points + Airtable area together. */
  function schedulePolySave(roomId) {
    if (pendingSyncRef.current[roomId]) clearTimeout(pendingSyncRef.current[roomId])
    pendingSyncRef.current[roomId] = setTimeout(() => persistPolyNow(roomId), SYNC_DEBOUNCE_MS)
  }

  function persistPolyNow(roomId) {
    if (pendingSyncRef.current[roomId]) { clearTimeout(pendingSyncRef.current[roomId]); delete pendingSyncRef.current[roomId] }
    const poly = st.current.polysByRoom[roomId]
    if (!poly || String(poly.id).startsWith('tmp_')) return
    const pts = poly.points.map(roundPt)
    const sf = Math.round(computeRegionValue('area', pts, frameRef.current, st.current.scale?.sppf) || 0)
    setPolysByRoom(prev => prev[roomId] ? { ...prev, [roomId]: { ...prev[roomId], points: pts, computedSf: sf } } : prev)
    saveRoomPolygon({ planId: poly.planId, roomId, points: pts, computedSf: sf })
      .then(() => writeAreaManual(roomId, sf))
      .catch(() => setSyncErrors(prev => ({ ...prev, [roomId]: true })))
  }

  function flushPendingSaves() {
    for (const roomId of Object.keys(pendingSyncRef.current)) persistPolyNow(roomId)
  }

  async function removePolygon(roomId, { isOrphan = false } = {}) {
    const poly = st.current.polysByRoom[roomId]
    if (!poly) return
    const rooms = Array.isArray(st.current.roomsByLevel[st.current.activeLevelId]) ? st.current.roomsByLevel[st.current.activeLevelId] : []
    const room = rooms.find(r => r.id === roomId)
    const name = room ? roomDisplayName(room) : 'this room'
    const ok = await confirmDialog({
      title: 'Delete this shape?',
      message: isOrphan
        ? 'Remove the shape left behind by a deleted room? This only removes it from the plan — nothing else changes.'
        : `This removes "${name}"'s shape from the plan only — the room itself is not deleted and stays in the room book. Its Manual Area Override will be cleared, so its area falls back to Length × Width until you redraw it.`,
      confirmLabel: 'Delete shape',
      danger: true,
    })
    if (!ok) return
    const prev = poly
    setPolysByRoom(p => { const out = { ...p }; delete out[roomId]; return out })
    if (st.current.selectedRoomId === roomId) setSelectedRoomId(null)
    try {
      await deleteRoomPolygon(roomId)
      // Legacy-tool parity: the polygon owned Area Manual, so deleting it clears the field.
      if (!isOrphan) writeAreaManual(roomId, null)
    } catch (e) {
      setPolysByRoom(p => ({ ...p, [roomId]: prev }))
      await alertDialog({ title: 'Delete failed', message: e.message || 'The shape could not be deleted.' })
    }
  }

  /* ── Upload / Replace plan ──────────────────────────────────────────────── */
  async function handlePlanFile(file) {
    const levelId = st.current.activeLevelId
    if (!file || !levelId) return
    const resolved = await resolveUpload(file)
    if (!resolved) return
    setUploading(true)
    try {
      const pdfjs = await loadPdfjs()
      if (!pdfjs) throw new Error('PDF viewer failed to load.')
      const buf = await resolved.arrayBuffer()
      const doc = await pdfjs.getDocument({ data: buf.slice(0) }).promise   // slice: getDocument detaches
      const page = await doc.getPage(1)
      const view = page.view
      const newFrame = { w: view[2] - view[0], h: view[3] - view[1], rotation: normalizeRotation(page.rotate || 0), ox: view[0], oy: view[1] }

      const { planId, priorPageFrame, pdfUpload } = await createPlanUploadUrl({ levelId, conceptId, projectId })
      await uploadPlanPdf(pdfUpload, resolved)

      // Fractions are frame-independent — polygons and scale POINTS carry over unchanged.
      // What can change is the physical scale (points per foot): re-derive it from the stored
      // reference distance on the new frame when the page shape matches; force recalibration
      // when it doesn't (the drawing almost certainly moved on the sheet).
      const prior = isFrame(priorPageFrame) ? priorPageFrame : null
      const oldScale = st.current.scale
      const finalize = { planId, pdfPath: pdfUpload.path, pageFrame: newFrame }
      let rescaled = null
      if (oldScale && prior) {
        if (framesEqual(prior, newFrame)) {
          // Same encoding — calibration still exact; leave scale fields untouched.
        } else if (aspectsMatch(prior, newFrame)) {
          const sppf = scalePointsPerFoot(oldScale.p1, oldScale.p2, newFrame, oldScale.feet)
          finalize.scalePoints = { p1: oldScale.p1, p2: oldScale.p2, feet: oldScale.feet }
          finalize.scalePointsPerFoot = sppf
          rescaled = sppf
        } else {
          finalize.scalePoints = null
          finalize.scalePointsPerFoot = null
        }
      }
      const plan = await finalizePlanUpload(finalize)
      pdfCacheRef.current[levelId] = undefined
      setPlansByLevel(prev => ({ ...prev, [levelId]: plan }))

      if (rescaled && rescaled !== oldScale.sppf) {
        // Recompute + resync every polygon area against the re-derived scale.
        const activePolys = Object.values(st.current.polysByRoom).filter(g => g.planId === planId)
        const areas = activePolys.map(g => ({
          roomId: g.roomId,
          computedSf: Math.round(computeRegionValue('area', g.points, newFrame, rescaled) || 0),
        }))
        if (areas.length) {
          await setPlanScale({ planId, scalePoints: finalize.scalePoints, scalePointsPerFoot: rescaled, areas })
          setPolysByRoom(prev => {
            const out = { ...prev }
            for (const a of areas) { if (out[a.roomId]) out[a.roomId] = { ...out[a.roomId], computedSf: a.computedSf } }
            return out
          })
          syncAreasToAirtable(areas)
        }
      }
      if (prior && oldScale) {
        const kept = finalize.scalePoints !== null
        await alertDialog({
          title: 'Plan replaced',
          message: kept
            ? 'Room shapes kept. The scale carried over — verify it against a known dimension, and adjust any that moved.'
            : 'Room shapes kept, but the new page shape differs — recalibrate the scale, then adjust any that moved.',
        })
      }
    } catch (e) {
      await alertDialog({ title: 'Upload failed', message: e.message || 'The plan PDF could not be uploaded.' })
    } finally {
      setUploading(false)
    }
  }

  /** SheetPlanPickerModal resolved an existing Drawing Sheet's PDF to a Blob (already fetched
   *  from its hosted URL — never the user's filesystem). Wrap it as a File and run it through
   *  the exact same pipeline a manual upload uses (page-frame calc, reprojection on replace,
   *  rescale sync) — no separate code path to keep in sync. */
  async function handleSheetPicked({ blob, name }) {
    setSheetPickerOpen(false)
    await handlePlanFile(new File([blob], name, { type: 'application/pdf' }))
  }

  /* ── Pointer interaction ────────────────────────────────────────────────── */
  function beginDrag(d) {
    dragRef.current = d
    window.addEventListener('pointermove', onDragMove)
    window.addEventListener('pointerup', onDragUp)
  }
  function onDragMove(e) {
    const d = dragRef.current
    if (!d) return
    if (d.mode === 'pan') { setVp(v => ({ ...v, x: d.vp0.x + (e.clientX - d.sx), y: d.vp0.y + (e.clientY - d.sy) })); return }
    const frac = evtFrac(e)
    if (d.mode === 'cal') {
      d.moved = true
      if (d.which === 'pending') { pendingP1Ref.current = frac; scheduleRender() }
      else setScale(s => s ? { ...s, [d.which]: frac } : s)
    } else if (d.mode === 'vertex') {
      d.moved = true
      setPolysByRoom(prev => {
        const p = prev[d.roomId]
        if (!p) return prev
        return { ...prev, [d.roomId]: { ...p, points: p.points.map((pt, i) => i === d.idx ? frac : pt) } }
      })
    } else if (d.mode === 'viewlabel') {
      d.moved = true
      const [sx2, sy2] = evtScreen(e)
      const dContent = { x: (sx2 - d.sx) / st.current.vp.z, y: (sy2 - d.sy) / st.current.vp.z }
      const feetDelta = contentDeltaToFeet(dContent.x, dContent.y)
      if (!feetDelta) return
      let ndx = d.ox + feetDelta.x, ndy = d.oy + feetDelta.y
      // Shift locks the drag to whichever cardinal axis (horizontal/vertical) it's closer to,
      // holding the OTHER axis at its value from the start of this drag — a standard
      // Shift-constrained-drag convention (Figma/PowerPoint), not a compass-direction lock.
      if (e.shiftKey) { if (Math.abs(feetDelta.x) > Math.abs(feetDelta.y)) ndy = d.oy; else ndx = d.ox }
      const unitId = findUnitForLevel(st.current.activeLevelId)
      if (!unitId) return
      setViewsByUnit(prev => {
        const list = prev[unitId] || []
        return { ...prev, [unitId]: list.map(v => v.id === d.viewId ? { ...v, callout: { ...v.callout, labelDx: ndx, labelDy: ndy } } : v) }
      })
    }
  }
  function onDragUp() {
    const d = dragRef.current
    dragRef.current = null
    window.removeEventListener('pointermove', onDragMove)
    window.removeEventListener('pointerup', onDragUp)
    if (!d) return
    if (d.mode === 'cal' && d.which !== 'pending' && d.moved) {
      const s = st.current.scale
      if (s && s.feet > 0) {
        persistScale(s.p1, s.p2, s.feet).then(next => {
          if (!next) return
          const totalIn = Math.round(next.feet * 12)
          setBubble({ p1: next.p1, p2: next.p2, feet: String(Math.floor(totalIn / 12)), inches: String(totalIn % 12) })
        })
      }
    } else if (d.mode === 'vertex' && d.moved) {
      schedulePolySave(d.roomId)
    } else if (d.mode === 'viewlabel' && d.moved) {
      const unitId = findUnitForLevel(st.current.activeLevelId)
      const v = unitId && (st.current.viewsByUnit[unitId] || []).find(x => x.id === d.viewId)
      if (v) updateViewCallout(v.id, v.callout).catch(() => { /* best-effort — next render still shows the local value */ })
    }
    scheduleRender()
  }

  function onPointerDown(e) {
    if (st.current.canvasState !== 'ready') return
    if (st.current.bubble) return
    if (e.button === 1 || spaceRef.current) { e.preventDefault(); beginDrag({ mode: 'pan', sx: e.clientX, sy: e.clientY, vp0: { ...st.current.vp } }); return }
    if (e.button !== 0) return
    const [sx, sy] = evtScreen(e)
    const frac = contentToFrac(...screenToContent(sx, sy))
    const t = st.current.tool

    if (t === 'select' || t === 'scale') {
      const cal = hitCalHandle(sx, sy)
      if (cal) { beginDrag({ mode: 'cal', which: cal.which }); return }
    }

    if (t === 'scale') {
      if (!pendingP1Ref.current) { pendingP1Ref.current = frac; scheduleRender(); return }
      const p1 = pendingP1Ref.current
      pendingP1Ref.current = null
      const s = st.current.scale
      const prefill = s && s.feet > 0 ? Math.round(s.feet * 12) : 0
      setBubble({ p1, p2: frac, feet: prefill ? String(Math.floor(prefill / 12)) : '', inches: prefill ? String(prefill % 12) : '' })
      return
    }

    if (t === 'draw' && st.current.drawRoomId) {
      const d = draftRef.current
      if (d && d.pts.length >= 3) {
        const first = fracToScreen(d.pts[0])
        if (Math.hypot(sx - first[0], sy - first[1]) <= CLOSE_PX) { commitDraft(); return }
      }
      if (!d) draftRef.current = { pts: [frac] }
      else d.pts.push(frac)
      scheduleRender()
      return
    }

    if (t === 'anchor') { persistAnchor(frac); return }

    if ((t === 'elevation' || t === 'section') && st.current.viewDraftUnitId) {
      const d = viewDraftRef.current || { pts: [] }
      d.pts.push(frac)
      viewDraftRef.current = d
      const need = t === 'elevation' ? 2 : 3
      if (d.pts.length >= need) commitViewDraft(t)
      else scheduleRender()
      return
    }

    // Select tool
    // View label grip (of the already-selected view) takes priority — it sits off the
    // marker's own path, same reasoning as SiteAnalysisModal's label-grip-before-item-hit-test.
    const grip = hitViewLabelGrip(sx, sy)
    if (grip) {
      const v = (st.current.viewsByUnit[findUnitForLevel(st.current.activeLevelId)] || []).find(x => x.id === grip)
      if (v) {
        const c = v.callout
        beginDrag({ mode: 'viewlabel', viewId: grip, sx, sy, ox: c.labelDx || 0, oy: c.labelDy != null ? c.labelDy : DEFAULT_LABEL_FEET_DY })
        return
      }
    }
    const vm = hitViewMarker(sx, sy)
    if (vm) { setSelectedViewId(vm); setSelectedRoomId(null); setSelection(null); return }
    const vh = hitVertexHandle(sx, sy)
    if (vh) { beginDrag({ mode: 'vertex', roomId: vh.roomId, idx: vh.idx }); return }
    // Alt-click a segment of the selected polygon inserts a vertex there — the main way to
    // add detail when adjusting a polygon to a replaced/evolved plan.
    if (e.altKey) {
      const seg = hitSegment(sx, sy)
      if (seg) {
        setPolysByRoom(prev => {
          const p = prev[seg.roomId]
          if (!p) return prev
          const pts = p.points.slice()
          pts.splice(seg.after + 1, 0, roundPt(frac))
          return { ...prev, [seg.roomId]: { ...p, points: pts } }
        })
        schedulePolySave(seg.roomId)
        return
      }
    }
    const hit = hitPoly(sx, sy)
    if (hit) { setSelectedRoomId(hit.roomId); setSelectedViewId(null); setSelection({ kind: 'room', id: hit.roomId }); return }
    setSelectedRoomId(null)
    setSelectedViewId(null)
    setSelection(null)
    beginDrag({ mode: 'pan', sx: e.clientX, sy: e.clientY, vp0: { ...st.current.vp } })
  }

  function onDoubleClick() {
    if (st.current.tool === 'draw' && draftRef.current) commitDraft()
  }

  function onCanvasMove(e) {
    const t = st.current.tool
    if (t === 'scale' || (t === 'draw' && st.current.drawRoomId) || t === 'elevation' || t === 'section') {
      hoverRef.current = evtFrac(e)
      scheduleRender()
    }
  }

  /* ── Wheel zoom (non-passive) ───────────────────────────────────────────── */
  useEffect(() => {
    const cont = containerRef.current
    if (!cont) return
    function onWheel(e) {
      if (!rasterBaseRef.current) return
      e.preventDefault()
      const v = st.current.vp
      const ns = clamp(+(v.z * (e.deltaY < 0 ? 1.12 : 1 / 1.12)).toFixed(3), 0.03, 8)
      if (ns === v.z) return
      const r = compRef.current.getBoundingClientRect()
      const lx = e.clientX - r.left, ly = e.clientY - r.top
      const f = 1 - ns / v.z
      rasterHiRef.current = null
      setVp({ x: v.x + (lx - v.x) * f, y: v.y + (ly - v.y) * f, z: ns })
      if (resharpenRef.current) clearTimeout(resharpenRef.current)
      resharpenRef.current = setTimeout(resharpen, RESHARPEN_MS)
    }
    cont.addEventListener('wheel', onWheel, { passive: false })
    return () => cont.removeEventListener('wheel', onWheel)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  function zoomBtn(dir) {
    const v = st.current.vp
    const ns = clamp(+(v.z * (dir > 0 ? 1.25 : 1 / 1.25)).toFixed(3), 0.03, 8)
    const cont = containerRef.current
    const cx = cont ? cont.clientWidth / 2 : 0, cy = cont ? cont.clientHeight / 2 : 0
    const f = 1 - ns / v.z
    rasterHiRef.current = null
    setVp({ x: v.x + (cx - v.x) * f, y: v.y + (cy - v.y) * f, z: ns })
    if (resharpenRef.current) clearTimeout(resharpenRef.current)
    resharpenRef.current = setTimeout(resharpen, RESHARPEN_MS)
  }

  function centerPoly(poly) {
    const cont = containerRef.current
    if (!cont || !rasterBaseRef.current || !Array.isArray(poly.points) || !poly.points.length) return
    const C = poly.points.map(p => fracToContent(p))
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
    for (const p of C) { minX = Math.min(minX, p[0]); minY = Math.min(minY, p[1]); maxX = Math.max(maxX, p[0]); maxY = Math.max(maxY, p[1]) }
    const b = { x: minX, y: minY, w: Math.max(maxX - minX, 20), h: Math.max(maxY - minY, 20) }
    const cw = cont.clientWidth, ch = cont.clientHeight, pad = 140
    const z = clamp(Math.min((cw - pad) / b.w, (ch - pad) / b.h), 0.05, 2.5)
    rasterHiRef.current = null
    setVp({ x: cw / 2 - (b.x + b.w / 2) * z, y: ch / 2 - (b.y + b.h / 2) * z, z })
    setSelectedRoomId(poly.roomId)
    if (resharpenRef.current) clearTimeout(resharpenRef.current)
    resharpenRef.current = setTimeout(resharpen, RESHARPEN_MS)
  }

  /* ── Keyboard — capture phase + stopPropagation so the outline never hears us ── */
  useEffect(() => {
    function onKey(e) {
      const ae = document.activeElement
      if (ae && (ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA' || ae.tagName === 'SELECT' || ae.isContentEditable)) {
        if (e.key === 'Escape' && st.current.bubble) { e.preventDefault(); e.stopPropagation(); cancelBubble() }
        return
      }
      if (st.current.addRoomLevelId) {
        // RoomFormModal (reused from the outline) has no Escape handling of its own — close
        // just the add-room form here rather than falling through to the tool's own Escape
        // chain, which would otherwise close the whole tool out from under the form.
        if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setAddRoomLevelId(null) }
        return
      }
      if (st.current.sheetPickerOpen) {
        if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setSheetPickerOpen(false) }
        return
      }
      if (st.current.addViewUnitId) {
        if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setAddViewUnitId(null) }
        return
      }
      if (e.key === ' ') { e.preventDefault(); e.stopPropagation(); spaceRef.current = true; return }
      if (e.key === 'Escape') {
        e.preventDefault(); e.stopPropagation()
        if (st.current.bubble) { cancelBubble(); return }
        if (pendingP1Ref.current) { pendingP1Ref.current = null; scheduleRender(); return }
        if (draftRef.current) { cancelDraft(); return }
        if (viewDraftRef.current) { cancelViewDraft(); return }
        if (st.current.tool !== 'select') { setTool('select'); setDrawRoomId(null); return }
        if (st.current.selectedViewId) { setSelectedViewId(null); return }
        if (st.current.selectedRoomId || st.current.selection) { setSelectedRoomId(null); setSelection(null); return }
        flushPendingSaves()
        onClose()
        return
      }
      if ((e.key === 'Delete' || e.key === 'Backspace') && st.current.selectedRoomId) {
        e.preventDefault(); e.stopPropagation()
        removePolygon(st.current.selectedRoomId)
        return
      }
      if (e.key === 'v' || e.key === 'V') { e.stopPropagation(); setTool('select'); setDrawRoomId(null); cancelDraft(); return }
      if (e.key === 's' || e.key === 'S') { e.stopPropagation(); startScaleTool(); return }
      // Reserve the outline's other nav keys too — the tool is a full-screen surface.
      if (['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight', 'Enter', 'x', '/'].includes(e.key)) {
        e.stopPropagation()
      }
    }
    function onKeyUp(e) { if (e.key === ' ') { spaceRef.current = false; e.stopPropagation() } }
    document.addEventListener('keydown', onKey, true)
    document.addEventListener('keyup', onKeyUp, true)
    return () => { document.removeEventListener('keydown', onKey, true); document.removeEventListener('keyup', onKeyUp, true) }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  function startScaleTool() {
    if (st.current.canvasState !== 'ready') return
    setDrawRoomId(null); cancelDraft(); setSelectedRoomId(null)
    pendingP1Ref.current = null
    setTool('scale')
  }

  /** Re-setting an existing anchor shifts every View's rendered position on this level (not on
   *  other levels — each level's own anchor is independent) — confirm before arming. */
  async function startAnchorTool() {
    if (st.current.canvasState !== 'ready') return
    const existing = st.current.plansByLevel[st.current.activeLevelId]?.anchorPoint
    if (existing) {
      const ok = await confirmDialog({
        title: 'Re-set alignment anchor?',
        message: 'This shifts where every View renders on this level. Views on other levels are unaffected.',
        confirmLabel: 'Re-set anchor', danger: true,
      })
      if (!ok) return
    }
    setDrawRoomId(null); cancelDraft(); setSelectedRoomId(null); cancelViewDraft()
    pendingP1Ref.current = null
    setTool('anchor')
  }

  /** "Draw in Sketch" — open the view's existing linked sketch, or (first time) create a
   *  genuinely blank one (no pre-seeded background — an elevation/section is a different view
   *  angle than the plan) and link it before opening, mirroring handleSendToSketch's own
   *  create-then-open handoff (there is no return channel from the /sketch tab). */
  async function handleDrawViewInSketch(view) {
    if (view.sourceType === 'sketch' && view.sketchId) {
      window.open('/sketch?id=' + view.sketchId, '_blank', 'noopener')
      return
    }
    try {
      const doc = { version: 2, scalePixPerFoot: null, scaleUnit: 'imperial', vp: { x: 40, y: 40, z: 1 }, layers: [], measurements: [], textObjects: [] }
      const jsonBlob = new Blob([JSON.stringify(doc)], { type: 'application/json' })
      const newId = await saveSketch(null, view.name + ' — ' + conceptName, jsonBlob, null)
      const updated = await setViewSketch(view.id, newId)
      setViewsByUnit(prev => ({ ...prev, [view.unitId]: (prev[view.unitId] || []).map(v => v.id === view.id ? updated : v) }))
      window.open('/sketch?id=' + newId, '_blank', 'noopener')
    } catch (e) {
      await alertDialog({ title: 'Could not open Sketch', message: e.message || 'Unknown error.' })
    }
  }

  /** Upload an image or single PDF page as the view's source — reuses the same page-picker
   *  (resolveUpload) already wired for level-plan uploads; multi-page PDFs prompt to pick a
   *  page, images pass through untouched. No separate thumbnail generation in v1 — the row
   *  shows the raw uploaded file directly. */
  async function handleUploadView(view, file) {
    const resolved = await resolveUpload(file)
    if (!resolved) return
    try {
      const viewUpload = await createViewUploadUrl(view.id)
      await uploadViewFile(viewUpload, resolved, resolved.type || 'application/octet-stream')
      const updated = await finalizeViewUpload({ viewId: view.id, uploadPath: viewUpload.path })
      setViewsByUnit(prev => ({ ...prev, [view.unitId]: (prev[view.unitId] || []).map(v => v.id === view.id ? updated : v) }))
    } catch (e) {
      await alertDialog({ title: 'Upload failed', message: e.message || 'The file could not be uploaded.' })
    }
  }

  async function handleRenameView(view) {
    const name = await promptDialog({ title: 'Rename view', defaultValue: view.name, required: true })
    if (name === null) return
    const updated = await renameViewSvc(view.id, name.trim() || view.name)
    setViewsByUnit(prev => ({ ...prev, [view.unitId]: (prev[view.unitId] || []).map(v => v.id === view.id ? updated : v) }))
  }

  /** "Move / Reorient" from the Views-list ⋯ menu — re-arms the same click-sequence placement UI
   *  (armView/commitViewDraft) in edit mode, targeting the view's original level if it still
   *  exists in this unit (falling back to the unit's first level). If a level switch is needed,
   *  defers the actual arm to the pendingArm effect above rather than calling armView directly —
   *  st.current.activeLevelId wouldn't reflect switchLevel's state update until the next render. */
  async function handleEditView(view) {
    const levels = levelsByUnit[view.unitId] || []
    const target = levels.find(l => l.id === view.originLevelId) || levels[0]
    if (!target) { await alertDialog({ title: 'Cannot edit', message: 'This view\'s unit has no levels to place it on.' }); return }
    if (!plansByLevel[target.id]?.anchorPoint) {
      await alertDialog({ title: 'Set the scale first', message: 'That level needs an alignment anchor before this view can be repositioned — set one from its Level Properties, then try again.' })
      return
    }
    if (target.id !== activeLevelId) switchLevel(target.id, { keepSelection: true })
    setPendingArm({ unitId: view.unitId, viewType: view.viewType, editingViewId: view.id })
  }

  /** Commits a drag-handle reorder — ViewsList already reordered its own local state live during
   *  the drag; this just makes viewsByUnit (the source of truth) match on drop, then persists. */
  async function handleReorderViews(unitId, orderedIds) {
    setViewsByUnit(prev => {
      const list = prev[unitId] || []
      const byId = new Map(list.map(v => [v.id, v]))
      const reordered = orderedIds.map(id => byId.get(id)).filter(Boolean)
      return { ...prev, [unitId]: reordered }
    })
    try {
      await reorderUnitViews(orderedIds)
    } catch (e) {
      await alertDialog({ title: 'Reorder not saved', message: e.message || 'The new order could not be saved. Try again.' })
    }
  }

  async function handleDeleteView(view) {
    const ok = await confirmDialog({
      title: 'Delete view?',
      message: 'Delete "' + view.name + '"? This removes its callout and any uploaded file. A linked Sketch itself is not deleted.',
      confirmLabel: 'Delete', danger: true,
    })
    if (!ok) return
    try {
      await deleteUnitView(view.id)
      setViewsByUnit(prev => ({ ...prev, [view.unitId]: (prev[view.unitId] || []).filter(v => v.id !== view.id) }))
    } catch (e) {
      await alertDialog({ title: 'Delete failed', message: e.message || 'The view could not be deleted.' })
    }
  }

  async function armDraw(roomId) {
    if (st.current.canvasState !== 'ready') return
    if (!scaleReady()) {
      await alertDialog({ title: 'Set the scale first', message: 'This level’s plan has no scale yet. Use Calibrate (press s) to set a known distance, then place room shapes.' })
      return
    }
    draftRef.current = null
    setSelectedRoomId(null)
    setDrawRoomId(roomId)
    setTool('draw')
  }

  function switchLevel(levelId, { keepSelection = false } = {}) {
    if (levelId === st.current.activeLevelId) return
    flushPendingSaves()
    setTool('select'); setDrawRoomId(null)
    if (!keepSelection) { setSelectedRoomId(null); setSelection(null) }
    setActiveLevelId(levelId)
  }

  /* ── Derived ────────────────────────────────────────────────────────────── */
  const drawRoom = drawRoomId && activeRooms ? activeRooms.find(r => r.id === drawRoomId) : null

  const bubblePos = useMemo(() => {
    if (!bubble || !rasterBaseRef.current || !containerRef.current) return null
    const a = fracToScreen(bubble.p1), b = fracToScreen(bubble.p2)
    const cont = containerRef.current
    const bw = 252, bh = 118
    const x = clamp((a[0] + b[0]) / 2 - bw / 2, 8, Math.max(8, cont.clientWidth - bw - 8))
    const y = clamp((a[1] + b[1]) / 2 - bh - 16, 8, Math.max(8, cont.clientHeight - bh - 8))
    return { left: x, top: y, width: bw }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bubble, vp])

  const activePlanId = activePlan?.id
  const orphanPolys = useMemo(() => {
    if (!activePlanId || !activeRooms) return []
    const ids = new Set(activeRooms.map(r => r.id))
    return Object.values(polysByRoom).filter(p => p.planId === activePlanId && !ids.has(p.roomId))
  }, [polysByRoom, activePlanId, activeRooms])

  // The selected entity's records for the properties palette. A selected room may live on a
  // non-active level (tree click mid-switch) — search every loaded level, not just the active one.
  const selectedRoom = selection?.kind === 'room'
    ? Object.values(roomsByLevel).reduce((found, rooms) => found || (Array.isArray(rooms) ? rooms.find(r => r.id === selection.id) : null), null)
    : null
  const selectedLevel = selection?.kind === 'level' ? findLevel(selection.id) : null
  const selectedUnit = selection?.kind === 'unit' ? (units || []).find(u => u.id === selection.id) : null
  const cursor = tool === 'select' ? 'default' : 'crosshair'

  /* ── Render ─────────────────────────────────────────────────────────────── */
  return createPortal(
    <div className="aj-pm-overlay" data-modal-id="ProgramModelModal" style={{ position: 'fixed', inset: 0, zIndex: 760, background: 'var(--aj-bg-page)', display: 'flex', flexDirection: 'column' }}>
      {/* Hover states for this tool's buttons. Uses !important where a rule needs to beat an
          inline resting style on the same property — same pattern outline.css itself uses for
          its own hover overrides (e.g. .wf-row:hover .wf-menu-btn opacity).
          .wf-menu-btn defaults to opacity:0, only lifted by outline.css's OWN ".wf-row:hover"
          rule — these room rows aren't outline rows, so that cascade never fires; the ⋯ trigger
          was invisible at every state, not just unhovered. Give it its own resting/hover opacity. */}
      <style>{`
        .aj-pm-tree-row .wf-menu-btn { opacity: 0.55; }
        .aj-pm-tree-row:hover .wf-menu-btn { opacity: 0.85; }
        .aj-pm-tree-row .wf-menu-btn:hover { opacity: 1; color: #07272D; background: rgba(7,39,45,0.08); }
        body.dark .aj-pm-tree-row .wf-menu-btn:hover { color: var(--aj-text); background: rgba(255,255,255,0.09); }
        .aj-pm-tree-row:hover { background: var(--aj-bg-subtle); }
        .aj-pm-topbtn:hover:not(:disabled) { background: rgba(255,255,255,0.28) !important; }
        .aj-pm-closebtn:hover { opacity: 0.7; }
        .aj-pm-ghostbtn:hover { background: var(--aj-bg-subtle) !important; color: var(--aj-text) !important; border-color: var(--aj-text-mid) !important; }
        .aj-pm-primarybtn:hover:not(:disabled) { background: #0d3540 !important; }
        .aj-pm-minibtn:hover:not(:disabled) { background: var(--aj-bg-subtle) !important; color: var(--aj-text) !important; border-color: var(--aj-text-mid) !important; }
        .aj-pm-dangerbtn:hover:not(:disabled) { background: rgba(192,57,43,0.08) !important; border-color: #c0392b !important; }
        .aj-pm-sheetrow:hover:not(:disabled) { background: var(--aj-bg-subtle) !important; }
        .aj-pm-divider { width: 5px; flex-shrink: 0; cursor: col-resize; background: transparent; transition: background 0.12s; }
        .aj-pm-divider:hover, .aj-pm-divider:active { background: var(--aj-border-mid); }
        .aj-pm-pal-input { transition: border-color 0.12s, background 0.12s; }
        .aj-pm-pal-input:hover { border-color: var(--aj-text-mid) !important; }
        .aj-pm-pal-input:focus { border-color: var(--aj-accent-blue) !important; background: var(--aj-bg-surface) !important; }
        .aj-pm-pal-actions .wf-menu-btn { opacity: 0.55; }
        .aj-pm-pal-actions .wf-menu-btn:hover { opacity: 1; color: #07272D; background: rgba(7,39,45,0.08); }
        body.dark .aj-pm-pal-actions .wf-menu-btn:hover { color: var(--aj-text); background: rgba(255,255,255,0.09); }
      `}</style>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', background: DARK, color: '#fff', flexShrink: 0 }}>
        <button onClick={() => setLeftOpen(o => !o)} className="aj-pm-topbtn" style={{ ...topBtn(false), padding: '3px 8px' }} title={leftOpen ? 'Hide properties' : 'Show properties'}>☰</button>
        <span style={{ fontFamily: '"Montserrat", sans-serif', fontWeight: 700, fontSize: 12, letterSpacing: '0.06em', textTransform: 'uppercase', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          Building Information Model · {conceptName}
        </span>
        <div style={{ flex: 1 }} />
        <button onClick={() => fileInputRef.current?.click()} disabled={!activeLevelId || uploading} className="aj-pm-topbtn" style={{ ...topBtn(false), opacity: (!activeLevelId || uploading) ? 0.5 : 1 }}
          title={activePlan?.pdfUrl ? 'Replace this level’s plan PDF' : 'Upload a plan PDF for this level'}>
          {uploading ? 'Uploading…' : (activePlan?.pdfUrl ? 'Replace plan' : 'Upload plan')}
        </button>
        <button onClick={() => setSheetPickerOpen(true)} disabled={!activeLevelId || !projectId || uploading} className="aj-pm-topbtn" style={{ ...topBtn(false), opacity: (!activeLevelId || !projectId || uploading) ? 0.5 : 1 }}
          title="Use an already-completed Drawing Sheet's PDF instead of uploading one">
          Use existing sheet
        </button>
        <button onClick={handleSendToSketch} disabled={!activeLevelId || canvasState !== 'ready' || sendingToSketch} className="aj-pm-topbtn"
          style={{ ...topBtn(false), opacity: (!activeLevelId || canvasState !== 'ready' || sendingToSketch) ? 0.5 : 1 }}
          title="Open this level's background, already scaled, as a new layer in the Sketch tool">
          {sendingToSketch ? 'Sending…' : 'Send to AlderSKETCH'}
        </button>
        <button onClick={() => { setTool('select'); setDrawRoomId(null); cancelDraft() }} className="aj-pm-topbtn" style={topBtn(tool === 'select')} title="Select and pan (v)">Select</button>
        <button onClick={startScaleTool} disabled={canvasState !== 'ready'} className="aj-pm-topbtn" style={{ ...topBtn(tool === 'scale'), opacity: canvasState !== 'ready' ? 0.5 : 1 }} title="Calibrate the plan scale (s)">Calibrate</button>
        <span style={{ width: 1, height: 18, background: 'rgba(255,255,255,0.25)' }} />
        <button onClick={() => zoomBtn(-1)} className="aj-pm-topbtn" style={topBtn(false)}>−</button>
        <span style={{ fontFamily: '"DM Mono", monospace', fontSize: 11, minWidth: 40, textAlign: 'center' }}>{Math.round(vp.z * 100)}%</span>
        <button onClick={() => zoomBtn(1)} className="aj-pm-topbtn" style={topBtn(false)}>+</button>
        <span style={{ width: 1, height: 18, background: 'rgba(255,255,255,0.25)' }} />
        {(savingScale || syncing > 0) && (
          <span style={{ fontFamily: '"DM Mono", monospace', fontSize: 11, color: '#9fb4ba' }}>
            {savingScale ? 'Saving…' : 'Syncing areas…'}
          </span>
        )}
        <button onClick={() => { flushPendingSaves(); onClose() }} className="aj-pm-closebtn" style={{ background: 'none', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer', lineHeight: 1 }} title="Close">×</button>
      </div>

      <input ref={fileInputRef} type="file" accept="application/pdf" style={{ display: 'none' }}
        onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) handlePlanFile(f) }} />

      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Left panel — Properties palette for the current selection */}
        {leftOpen && (
          <>
            <div style={{ width: propW, flexShrink: 0, background: 'var(--aj-bg-elevated)', borderRight: '0.5px solid var(--aj-border-mid)', overflowY: 'auto' }}>
              <PropertiesPanel
                selection={selection}
                room={selectedRoom}
                level={selectedLevel}
                unit={selectedUnit}
                units={units}
                levelsByUnit={levelsByUnit}
                plansByLevel={plansByLevel}
                polysByRoom={polysByRoom}
                levelRollup={levelRollup}
                unitRollup={unitRollup}
                findLevel={findLevel}
                syncErrors={syncErrors}
                canvasReady={canvasState === 'ready'}
                scaleReady={scaleReady()}
                onSaveRoom={saveRoomFields}
                onPlace={roomId => armDraw(roomId)}
                onDeletePolygon={roomId => removePolygon(roomId)}
                onCenter={roomId => { const p = polysByRoom[roomId]; if (p && p.planId === activePlanId) centerPoly(p) }}
                onRetry={roomId => retrySync(roomId)}
                onAddRoom={levelId => setAddRoomLevelId(levelId)}
                onSetAnchor={startAnchorTool}
                viewsByUnit={viewsByUnit}
                onAddView={unitId => {
                  // A View's placement operates on whatever level is currently on canvas — if
                  // that level belongs to a DIFFERENT unit than the one whose "+ Add view" was
                  // clicked (e.g. the user was looking at Main House and clicked into ADU
                  // Detached's properties without visiting one of its own levels first), switch
                  // to that unit's own first level BEFORE opening the picker, so the modal's
                  // anchorReady check (and the eventual armView) reflect the right level.
                  if (findUnitForLevel(activeLevelId) !== unitId) {
                    const firstLevel = (levelsByUnit[unitId] || [])[0]
                    if (firstLevel) switchLevel(firstLevel.id, { keepSelection: true })
                  }
                  setAddViewUnitId(unitId)
                }}
                onDrawViewInSketch={handleDrawViewInSketch}
                onUploadView={handleUploadView}
                onRenameView={handleRenameView}
                onDeleteView={handleDeleteView}
                onReorderViews={handleReorderViews}
                onEditView={handleEditView}
                onSetLevelElevation={handleSetLevelElevation}
              />
            </div>
            <div className="aj-pm-divider" onPointerDown={e => startPanelResize('left', propW, e)} title="Drag to resize" />
          </>
        )}

        {/* Canvas */}
        <div ref={containerRef} style={{ flex: 1, position: 'relative', overflow: 'hidden', background: 'var(--aj-bg-muted)', cursor }}>
          <canvas
            ref={compRef}
            onPointerDown={onPointerDown}
            onPointerMove={onCanvasMove}
            onDoubleClick={onDoubleClick}
            style={{ display: 'block', position: 'absolute', left: 0, top: 0, touchAction: 'none' }}
          />

          {/* Armed-room chip */}
          {tool === 'draw' && drawRoom && (
            <div style={{ position: 'absolute', top: 10, left: '50%', transform: 'translateX(-50%)', display: 'flex', alignItems: 'center', gap: 8, background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 20, padding: '5px 12px', boxShadow: '0 3px 10px rgba(0,0,0,0.14)', maxWidth: '80%' }}>
              <span style={{ width: 10, height: 10, borderRadius: 3, background: roomColor(drawRoom.id), flexShrink: 0 }} />
              <span style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 12, fontWeight: 600, color: 'var(--aj-text)', whiteSpace: 'nowrap' }}>{roomDisplayName(drawRoom)}</span>
              <span style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 11, color: 'var(--aj-text-mid)', whiteSpace: 'nowrap' }}>Click to add vertices. Click the first vertex or double-click to close.</span>
              <button onClick={() => { setTool('select'); setDrawRoomId(null); cancelDraft() }} style={{ background: 'none', border: 'none', color: 'var(--aj-text-muted)', cursor: 'pointer', fontSize: 13, padding: 0, lineHeight: 1 }} title="Stop drawing (Esc)">×</button>
            </div>
          )}

          {/* Armed-view chip — new placement or an in-place edit (Move / Reorient) */}
          {(tool === 'elevation' || tool === 'section') && viewDraftUnitId && (() => {
            const editingId = viewDraftRef.current?.editingViewId
            const editingView = editingId ? (viewsByUnit[viewDraftUnitId] || []).find(v => v.id === editingId) : null
            return (
              <div style={{ position: 'absolute', top: 10, left: '50%', transform: 'translateX(-50%)', display: 'flex', alignItems: 'center', gap: 8, background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 20, padding: '5px 12px', boxShadow: '0 3px 10px rgba(0,0,0,0.14)', maxWidth: '80%' }}>
                <span style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 12, fontWeight: 600, color: 'var(--aj-text)', whiteSpace: 'nowrap' }}>
                  {editingView ? 'Repositioning “' + editingView.name + '”' : (tool === 'elevation' ? 'New Elevation' : 'New Section')}
                </span>
                <span style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 11, color: 'var(--aj-text-mid)', whiteSpace: 'nowrap' }}>
                  {tool === 'elevation' ? 'Click a position, then a point for the direction it looks.' : "Click the cut line's two ends, then the side it looks toward."}
                </span>
                <button onClick={cancelViewDraft} style={{ background: 'none', border: 'none', color: 'var(--aj-text-muted)', cursor: 'pointer', fontSize: 13, padding: 0, lineHeight: 1 }} title="Cancel (Esc)">×</button>
              </div>
            )
          })()}

          {/* Scale-placement hint */}
          {tool === 'scale' && !bubble && (
            <div style={{ position: 'absolute', top: 10, left: '50%', transform: 'translateX(-50%)', background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 20, padding: '5px 14px', fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: 'var(--aj-text)', boxShadow: '0 3px 10px rgba(0,0,0,0.14)', whiteSpace: 'nowrap' }}>
              {pendingP1Ref.current ? 'Click the second point of a known distance' : 'Click the first point of a known distance (a dimension line works well)'}
            </div>
          )}

          {/* Distance bubble */}
          {bubble && bubblePos && (
            <div style={{ position: 'absolute', left: bubblePos.left, top: bubblePos.top, width: bubblePos.width, background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.22)', padding: 12 }}>
              <div style={{ fontFamily: '"Montserrat", sans-serif', fontSize: 10, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--aj-text-mid)', marginBottom: 8 }}>Real distance</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <input type="number" min="0" autoFocus value={bubble.feet} placeholder="0"
                  onChange={e => setBubble(b => b ? { ...b, feet: e.target.value } : b)}
                  onKeyDown={e => { if (e.key === 'Enter') applyBubble() }}
                  style={bubbleInput} />
                <span style={bubbleUnit}>ft</span>
                <input type="number" min="0" max="11" value={bubble.inches} placeholder="0"
                  onChange={e => setBubble(b => b ? { ...b, inches: e.target.value } : b)}
                  onKeyDown={e => { if (e.key === 'Enter') applyBubble() }}
                  style={bubbleInput} />
                <span style={bubbleUnit}>in</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 10 }}>
                <button onClick={cancelBubble} style={ghostBtn}>Cancel</button>
                <button onClick={applyBubble} disabled={savingScale || !(((parseFloat(bubble.feet) || 0) + (parseFloat(bubble.inches) || 0) / 12) > 0)}
                  style={{ ...primaryBtn, opacity: savingScale || !(((parseFloat(bubble.feet) || 0) + (parseFloat(bubble.inches) || 0) / 12) > 0) ? 0.5 : 1 }}>
                  {savingScale ? 'Saving…' : 'Apply'}
                </button>
              </div>
            </div>
          )}

          {canvasState === 'loading' && (
            <div style={centerBox}>
              <div style={{ width: 26, height: 26, border: '3px solid var(--aj-border)', borderTopColor: 'var(--aj-accent-blue)', borderRadius: '50%', animation: 'pmspin 0.8s linear infinite' }} />
              <span style={{ color: 'var(--aj-text-mid)', fontFamily: '"DM Sans", sans-serif', fontSize: 13 }}>Loading plan…</span>
              <style>{'@keyframes pmspin{to{transform:rotate(360deg)}}'}</style>
            </div>
          )}
          {canvasState === 'error' && (
            <div style={centerBox}>
              <div style={{ color: 'var(--aj-text)', fontFamily: '"Montserrat", sans-serif', fontSize: 14 }}>Could not open the plan</div>
              <div style={{ color: 'var(--aj-text-mid)', fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, maxWidth: 340, textAlign: 'center' }}>{canvasError}</div>
            </div>
          )}
          {canvasState === 'empty' && activeLevelId && (
            <div style={centerBox}>
              <div style={{ color: 'var(--aj-text)', fontFamily: '"Montserrat", sans-serif', fontSize: 13, letterSpacing: '0.05em', textTransform: 'uppercase' }}>No plan uploaded for this level</div>
              <div style={{ color: 'var(--aj-text-mid)', fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, maxWidth: 380, textAlign: 'center', lineHeight: 1.55 }}>
                Upload a plan PDF (a concept sketch works — replace it with the drawn plan as the design progresses), or use an already-completed Drawing Sheet. Calibrate the scale, then trace each room to keep its area live.
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                <button onClick={() => fileInputRef.current?.click()} disabled={uploading} className="aj-pm-primarybtn" style={primaryBtn}>
                  {uploading ? 'Uploading…' : 'Upload plan PDF'}
                </button>
                <button onClick={() => setSheetPickerOpen(true)} disabled={!projectId || uploading} className="aj-pm-ghostbtn" style={ghostBtn}>
                  Use existing sheet
                </button>
              </div>
            </div>
          )}
          {canvasState === 'ready' && !scaleReady() && !bubble && tool !== 'scale' && (
            <div style={{ position: 'absolute', bottom: 14, left: '50%', transform: 'translateX(-50%)', background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 20, padding: '6px 16px', fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: 'var(--aj-text)', boxShadow: '0 3px 10px rgba(0,0,0,0.14)', display: 'flex', alignItems: 'center', gap: 10 }}>
              <span>Calibrate the scale to begin placing rooms</span>
              <button onClick={startScaleTool} style={{ ...primaryBtn, padding: '3px 10px' }}>Calibrate</button>
            </div>
          )}
        </div>

        {/* Right panel — the Building tree: Units → Levels → Rooms */}
        <div className="aj-pm-divider" onPointerDown={e => startPanelResize('right', treeW, e)} title="Drag to resize" />
        <div style={{ width: treeW, flexShrink: 0, background: 'var(--aj-bg-elevated)', borderLeft: '0.5px solid var(--aj-border-mid)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ padding: '12px 14px 8px', borderBottom: '0.5px solid var(--aj-border)' }}>
            <div style={sideHeading}>Building</div>
            <div style={{ marginTop: 5, fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: 'var(--aj-text-muted)', lineHeight: 1.45 }}>
              Select a unit, level, or room to see its properties on the left.
            </div>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: '6px 0 20px' }}>
            {units === null && <div style={drawerMsg}>Loading…</div>}
            {Array.isArray(units) && !units.length && <div style={drawerMsg}>No units on this concept yet.</div>}
            {(units || []).map(u => {
              const uExpanded = expanded.has(u.id)
              return (
                <div key={u.id}>
                  <TreeRow
                    depth={0}
                    expandable
                    expanded={uExpanded}
                    onToggle={() => toggleExpand(u.id)}
                    onClick={() => selectEntity('unit', u.id)}
                    selected={selection?.kind === 'unit' && selection.id === u.id}
                    label={u.fields?.['Unit Type'] || u.fields?.['Name'] || 'Unit'}
                    bold
                  />
                  {uExpanded && (levelsByUnit[u.id] || []).map(l => {
                    const lExpanded = expanded.has(l.id)
                    const lRooms = roomsByLevel[l.id]
                    return (
                      <div key={l.id}>
                        <TreeLevelRow
                          level={l}
                          expanded={lExpanded}
                          onToggle={() => { toggleExpand(l.id); if (!lExpanded) ensureRooms(l.id) }}
                          onClick={() => selectEntity('level', l.id)}
                          selected={selection?.kind === 'level' && selection.id === l.id}
                          active={l.id === activeLevelId}
                          roomCount={Array.isArray(lRooms) ? lRooms.length : null}
                          onAddRoom={() => setAddRoomLevelId(l.id)}
                        />
                        {lExpanded && (
                          <>
                            {lRooms === null && <div style={{ ...drawerMsg, paddingLeft: 46 }}>Loading rooms…</div>}
                            {Array.isArray(lRooms) && !lRooms.length && <div style={{ ...drawerMsg, paddingLeft: 46 }}>No rooms yet</div>}
                            {(Array.isArray(lRooms) ? lRooms : []).map(room => {
                              const poly = polysByRoom[room.id] && polysByRoom[room.id].planId === plansByLevel[l.id]?.id ? polysByRoom[room.id] : null
                              return (
                                <TreeRoomRow
                                  key={room.id}
                                  room={room}
                                  poly={poly}
                                  selected={selection?.kind === 'room' && selection.id === room.id}
                                  syncError={!!syncErrors[room.id]}
                                  onClick={() => selectEntity('room', room.id, l.id)}
                                />
                              )
                            })}
                          </>
                        )}
                      </div>
                    )
                  })}
                  {uExpanded && Array.isArray(levelsByUnit[u.id]) && !levelsByUnit[u.id].length && (
                    <div style={{ ...drawerMsg, paddingLeft: 32 }}>No levels</div>
                  )}
                </div>
              )
            })}
            {orphanPolys.length > 0 && (
              <div style={{ padding: '10px 14px' }}>
                <div style={sideHeading}>Orphaned shapes</div>
                {orphanPolys.map(p => (
                  <div key={p.roomId} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0' }}>
                    <span style={{ flex: 1, fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, color: 'var(--aj-text-muted)', fontStyle: 'italic' }}>Room deleted in the room book</span>
                    <button onClick={() => removePolygon(p.roomId, { isOrphan: true })} className="aj-pm-minibtn" style={miniBtn}>Remove</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {picker}
      {addRoomLevelId && (
        <RoomFormModal
          title={'Add Room — ' + (findLevel(addRoomLevelId)?.fields?.['Name'] || 'Level')}
          initial={null}
          onSave={handleAddRoom}
          onClose={() => setAddRoomLevelId(null)}
        />
      )}
      {sheetPickerOpen && (
        <SheetPlanPickerModal
          projectId={projectId}
          onSelect={handleSheetPicked}
          onClose={() => setSheetPickerOpen(false)}
        />
      )}
      {addViewUnitId && (
        <AddViewModal
          anchorReady={!!plansByLevel[activeLevelId]?.anchorPoint}
          onSetAnchor={() => { setAddViewUnitId(null); startAnchorTool() }}
          onChoose={viewType => { const unitId = addViewUnitId; setAddViewUnitId(null); armView(unitId, viewType) }}
          onClose={() => setAddViewUnitId(null)}
        />
      )}
    </div>,
    document.body
  )
}

/* ══════════════════════ Building tree rows ══════════════════════ */

function TreeChevron({ expanded, onToggle }) {
  return (
    <button
      onClick={e => { e.stopPropagation(); onToggle() }}
      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-muted)', fontSize: 9, padding: '0 3px', width: 16, flexShrink: 0, lineHeight: 1 }}
      title={expanded ? 'Collapse' : 'Expand'}
    >{expanded ? '▾' : '▸'}</button>
  )
}

/** Generic tree row — used directly for units; levels and rooms wrap it with their extras. */
function TreeRow({ depth, expandable, expanded, onToggle, onClick, selected, label, bold, children }) {
  return (
    <div
      className="aj-pm-tree-row"
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer',
        padding: '5px 10px 5px ' + (10 + depth * 22) + 'px',
        background: selected ? withAlpha(SELECT_COLOR, 0.07) : undefined,
        borderLeft: selected ? '2px solid ' + SELECT_COLOR : '2px solid transparent',
      }}>
      {expandable ? <TreeChevron expanded={expanded} onToggle={onToggle} /> : <span style={{ width: 16, flexShrink: 0 }} />}
      <span style={{ flex: 1, fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: bold ? 600 : 400, color: 'var(--aj-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {label}
      </span>
      {children}
    </div>
  )
}

/** Level row — active-level marker, room count, ⋯ menu (Add room). Separate component
 *  because useActionMenu is a hook (one call per row). No status dot here — the colored
 *  dots belong to rooms only (they mirror each room's polygon color on the plan); a level
 *  isn't drawn in any color, so it doesn't get one. */
function TreeLevelRow({ level, expanded, onToggle, onClick, selected, active, roomCount, onAddRoom }) {
  const menu = useActionMenu()
  return (
    <div
      className="aj-pm-tree-row"
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer',
        padding: '5px 10px 5px 32px',
        background: selected ? withAlpha(SELECT_COLOR, 0.07) : (active ? 'var(--aj-bg-subtle)' : undefined),
        borderLeft: selected ? '2px solid ' + SELECT_COLOR : (active ? '2px solid ' + DARK : '2px solid transparent'),
      }}>
      <TreeChevron expanded={expanded} onToggle={onToggle} />
      <span style={{ flex: 1, fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: active ? 600 : 400, color: 'var(--aj-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {level.fields?.['Name'] || 'Level'}
      </span>
      {roomCount != null && <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 10, color: 'var(--aj-text-muted)', flexShrink: 0 }}>{roomCount}</span>}
      <ActionMenuButton menu={menu} title="Level actions" />
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={onAddRoom}>Add room</ActionMenuItem>
      </ActionMenu>
    </div>
  )
}

/** Room row — color dot, SF, sync-error dot. No ⋯ menu here — Draw/Redraw room, Delete shape
 *  live in the Properties palette on the left now that selecting this row opens it there. */
function TreeRoomRow({ room, poly, selected, syncError, onClick }) {
  const net = room.fields?.['Room Area Net']
  return (
    <div
      className="aj-pm-tree-row"
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer',
        padding: '4px 10px 4px 54px',
        background: selected ? withAlpha(SELECT_COLOR, 0.07) : undefined,
        borderLeft: selected ? '2px solid ' + SELECT_COLOR : '2px solid transparent',
      }}>
      <span style={{ width: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <span style={{ width: 9, height: 9, borderRadius: 3, background: roomColor(room.id), opacity: poly ? 1 : 0.35, flexShrink: 0 }} />
      </span>
      <span style={{ flex: 1, fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: poly ? 600 : 400, color: poly ? 'var(--aj-text)' : 'var(--aj-text-mid)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {roomDisplayName(room)}
      </span>
      {syncError && <span title="Area not synced to the room book — open the room's properties to retry" style={{ width: 7, height: 7, borderRadius: '50%', background: '#c0392b', flexShrink: 0 }} />}
      <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 10.5, color: poly ? 'var(--aj-text)' : 'var(--aj-text-muted)', flexShrink: 0 }}>
        {poly ? fmtSF(poly.computedSf || 0) : (net > 0 ? fmtSF(net) : '—')}
      </span>
    </div>
  )
}

/* ══════════════════════ Properties palette ══════════════════════ */
/* Deliberately NOT the outline row format — a compact label/value palette. Editable fields are
   quiet inputs committing on blur/Enter; read-only values are DM Mono. Level and unit
   selections show ROLL-UPS (program + budget) summed from their rooms' Airtable records. */

// Collapsed state persists per section TITLE (not per room) in localStorage — collapsing
// "Budget" stays collapsed as you page through rooms/levels/units, matching how someone would
// actually use this (they've decided they don't need to see that section right now).
function PalSection({ title, first, children }) {
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('aj-pm-sec-' + title) === '1' } catch { return false }
  })
  function toggle() {
    setCollapsed(c => {
      const next = !c
      try { localStorage.setItem('aj-pm-sec-' + title, next ? '1' : '0') } catch { /* noop */ }
      return next
    })
  }
  return (
    <>
      <div onClick={toggle} style={{ fontFamily: '"Montserrat",sans-serif', fontSize: 10, fontWeight: 600, letterSpacing: '0.09em', textTransform: 'uppercase', color: 'var(--aj-text-mid)', padding: (first ? '2px' : '16px') + ' 14px 5px', borderBottom: '0.5px solid var(--aj-border)', margin: '0 0 4px', cursor: 'pointer', userSelect: 'none', display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ fontSize: 8, display: 'inline-block', transform: collapsed ? 'rotate(-90deg)' : 'none', transition: 'transform 0.12s' }}>▾</span>
        {title}
      </div>
      {!collapsed && children}
    </>
  )
}

function PalRow({ label, hint, children }) {
  return (
    <div style={{ padding: '3px 14px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: 'var(--aj-text-mid)', width: 108, flexShrink: 0 }}>{label}</span>
        <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 6 }}>{children}</div>
      </div>
      {hint && <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 10, color: 'var(--aj-text-muted)', lineHeight: 1.4, padding: '2px 0 0 118px' }}>{hint}</div>}
    </div>
  )
}

function PalVal({ children, muted, strong }) {
  return (
    <span style={{
      fontFamily: '"DM Mono",monospace', fontSize: strong ? 12.5 : 11.5,
      fontWeight: strong ? 500 : 400,
      color: muted ? 'var(--aj-text-muted)' : 'var(--aj-text)',
      fontStyle: muted ? 'italic' : 'normal',
      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
    }}>{children}</span>
  )
}

const palInputStyle = {
  flex: 1, minWidth: 0, fontFamily: '"DM Sans",sans-serif', fontSize: 12,
  padding: '4px 8px', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4,
  background: 'var(--aj-bg-subtle)', color: 'var(--aj-text)', outline: 'none', boxSizing: 'border-box',
}
const palNumStyle = { ...palInputStyle, fontFamily: '"DM Mono",monospace', textAlign: 'right', maxWidth: 110 }

/** Plain controlled inputs — the palette now stages edits in local draft state and only
 *  persists on an explicit Save (see PropertiesPanel's Room branch), so these just report
 *  every keystroke/selection back to the caller. No blur-commit, no key-based remount. */
function PalText({ value, placeholder, onChange }) {
  return (
    <input
      type="text" className="aj-pm-pal-input" value={value} placeholder={placeholder}
      style={palInputStyle}
      onChange={e => onChange(e.target.value)}
    />
  )
}

function PalNum({ value, unit, step, onChange }) {
  return (
    <>
      <input
        type="number" min="0" step={step || 1} className="aj-pm-pal-input" value={value}
        style={palNumStyle}
        onChange={e => onChange(e.target.value)}
      />
      {unit && <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 10.5, color: 'var(--aj-text-mid)', flexShrink: 0 }}>{unit}</span>}
    </>
  )
}

function PalSelect({ value, options, placeholder, onChange }) {
  return (
    <select
      className="aj-pm-pal-input" value={value}
      style={{ ...palInputStyle, background: 'var(--aj-bg-subtle)' }}
      onChange={e => onChange(e.target.value)}
    >
      <option value="">{placeholder || 'Select…'}</option>
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  )
}

function PropertiesPanel({
  selection, room, level, unit, units, levelsByUnit, plansByLevel, polysByRoom,
  levelRollup, unitRollup, findLevel, syncErrors, canvasReady, scaleReady,
  onSaveRoom, onPlace, onDeletePolygon, onCenter, onRetry, onAddRoom,
  onSetAnchor, viewsByUnit, onAddView, onDrawViewInSketch, onUploadView, onRenameView, onDeleteView, onReorderViews, onEditView,
  onSetLevelElevation,
}) {
  const templates = useRoomTemplates()
  const polyMenu = useActionMenu()
  // Edits are staged here and only persisted on an explicit Save — see the "Unsaved changes"
  // bar below. On a SELECTED-ROOM change the draft is fully reset. On the SAME room, a fields
  // refresh (the room's own prior save, or the polygon tool's automatic Area Manual sync —
  // drawing/editing a room's shape writes its SF back to Airtable in the background) is merged
  // in field-by-field, but ONLY for fields the draft hasn't already diverged from the last-synced
  // baseline — so an in-progress unsaved edit is never clobbered mid-type. Previously this only
  // reset on room?.id, so a background sync for the STILL-selected room (e.g. right after drawing
  // its polygon) never reached the visible Length/Width/Manual Override inputs — they stayed blank/
  // stale until something else happened to change room?.id (switching rooms and back, etc.).
  const [draft, setDraft] = useState(() => roomDraft(room))
  const [savingDraft, setSavingDraft] = useState(false)
  const roomIdRef = useRef(room?.id)
  const syncedRef = useRef(roomDraft(room))
  useEffect(() => {
    const fresh = roomDraft(room)
    if (room?.id !== roomIdRef.current) {
      roomIdRef.current = room?.id
      syncedRef.current = fresh
      setDraft(fresh)
      return
    }
    const prevSynced = syncedRef.current
    setDraft(d => {
      let next = null
      for (const key of Object.keys(fresh)) {
        const isArr = key === 'structuralCharacteristics'
        const untouched = isArr ? JSON.stringify(d[key]) === JSON.stringify(prevSynced[key]) : d[key] === prevSynced[key]
        const differs = isArr ? JSON.stringify(fresh[key]) !== JSON.stringify(d[key]) : fresh[key] !== d[key]
        if (untouched && differs) { if (!next) next = { ...d }; next[key] = fresh[key] }
      }
      return next || d
    })
    syncedRef.current = fresh
  }, [room])
  // Structural finish elevation — a plain free-text draft, committed on blur (not per-keystroke,
  // and not the heavier explicit-Save-bar pattern the Room branch uses — this is one field).
  // Resets whenever the selected LEVEL changes or its own saved value changes underneath us.
  const levelPlan = level ? plansByLevel[level.id] : null
  const [elevDraft, setElevDraft] = useState('')
  useEffect(() => {
    setElevDraft(levelPlan?.structuralFinishElevation != null ? String(levelPlan.structuralFinishElevation) : '')
  }, [level?.id, levelPlan?.structuralFinishElevation])

  if (!selection) {
    return (
      <div style={{ padding: '18px 16px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text-muted)', lineHeight: 1.6 }}>
        <div style={{ ...sideHeading, marginBottom: 8 }}>Properties</div>
        Nothing selected. Pick a room, level, or unit from the Building tree on the right — or click a
        room's shape on the plan — to see and edit its properties here. Levels and units show rolled-up
        program and budget totals.
      </div>
    )
  }

  /* ── ROOM ─────────────────────────────────────────────────────────────── */
  if (selection.kind === 'room') {
    if (!room) return <div style={{ padding: '18px 16px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text-muted)' }}>Loading room…</div>
    const f = room.fields || {}
    const poly = polysByRoom[room.id] || null
    const templateItems = templates ? templates.map(t => ({ id: t.id, label: t.name })) : null
    const low = f['Budget Room Cost Low'], high = f['Budget Room Cost High']
    const syncErr = !!syncErrors[room.id]

    const saved = roomDraft(room)
    const isDirty = Object.keys(draft).some(k => k === 'structuralCharacteristics'
      ? JSON.stringify(draft[k]) !== JSON.stringify(saved[k])
      : draft[k] !== saved[k])
    const missingRequired = !draft.customName.trim() || !draft.roomTemplateId || !draft.scopeType
    // Live preview of the UNSAVED draft only — mirrors the same formula Airtable's own Cost
    // Factor field now computes (Round 6), so this can show the number before Save without ever
    // writing it. The room's actual Cost Factor (read elsewhere via room.fields) is always
    // authoritative once saved.
    const derivedCostFactor = deriveCostFactor(templates, draft.roomTemplateId, draft.structuralCharacteristics)
    const selectedTemplate = (templates || []).find(t => t.id === draft.roomTemplateId)
    function setField(key, value) { setDraft(d => ({ ...d, [key]: value })) }
    function toggleCharacteristic(key, on) {
      setField('structuralCharacteristics', on
        ? [...draft.structuralCharacteristics, key]
        : draft.structuralCharacteristics.filter(c => c !== key))
    }

    async function handleSaveDraft() {
      if (missingRequired || savingDraft) return
      const patch = {}
      if (draft.customName !== saved.customName) patch['Custom Name'] = draft.customName.trim()
      if (draft.roomTemplateId !== saved.roomTemplateId) patch['Room Template'] = [draft.roomTemplateId]
      if (draft.scopeType !== saved.scopeType) patch['Scope Type'] = draft.scopeType
      if (draft.roomNumber !== saved.roomNumber) patch['Room Number'] = draft.roomNumber.trim()
      if (draft.length !== saved.length) patch['Length'] = draft.length === '' ? null : Number(draft.length)
      if (draft.width !== saved.width) patch['Width'] = draft.width === '' ? null : Number(draft.width)
      if (draft.areaManual !== saved.areaManual) patch['Area Manual'] = draft.areaManual === '' ? null : Number(draft.areaManual)
      const charsChanged = JSON.stringify(draft.structuralCharacteristics) !== JSON.stringify(saved.structuralCharacteristics)
      if (charsChanged) patch['Structural Characteristics'] = draft.structuralCharacteristics
      // Cost Factor is an Airtable FORMULA field now (Round 6) — never send it; Airtable derives
      // it live from Room Template + Structural Characteristics the instant either one saves.
      if (!Object.keys(patch).length) return
      setSavingDraft(true)
      try { await onSaveRoom(room.id, patch) }
      catch (e) { await alertDialog({ title: 'Not saved', message: e.message || 'The changes could not be saved.' }) }
      finally { setSavingDraft(false) }
    }

    return (
      <div style={{ paddingBottom: 24 }}>
        <div style={{ padding: '14px 14px 10px' }}>
          <div style={{ ...sideHeading, marginBottom: 3 }}>Room</div>
          <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 14, fontWeight: 600, color: 'var(--aj-text)' }}>{roomDisplayName(room)}</div>
        </div>

        {isDirty && (
          <div style={{ position: 'sticky', top: 0, zIndex: 2, display: 'flex', alignItems: 'center', gap: 8, padding: '7px 14px', background: 'var(--aj-bg-elevated)', borderTop: '0.5px solid var(--aj-border)', borderBottom: '0.5px solid var(--aj-border)', boxShadow: '0 2px 6px rgba(0,0,0,0.08)' }}>
            <span style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, fontWeight: 600, color: '#b45309' }}>Unsaved changes</span>
            <div style={{ flex: 1 }} />
            <button onClick={() => setDraft(saved)} disabled={savingDraft} className="aj-pm-ghostbtn" style={{ ...ghostBtn, opacity: savingDraft ? 0.5 : 1 }}>Discard</button>
            <button onClick={handleSaveDraft} disabled={missingRequired || savingDraft} className="aj-pm-primarybtn"
              style={{ ...primaryBtn, opacity: (missingRequired || savingDraft) ? 0.5 : 1 }}
              title={missingRequired ? 'Room Name, Room Template, and Scope Type are required' : undefined}>
              {savingDraft ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        )}

        <PalSection title="Identity" first>
          <PalRow label="Room Name">
            <PalText value={draft.customName} placeholder="e.g. Primary Bedroom" onChange={v => setField('customName', v)} />
          </PalRow>
          <PalRow label="Room Template">
            <div style={{ flex: 1, minWidth: 0 }}>
              <SearchablePicker items={templateItems} value={draft.roomTemplateId} onChange={id => setField('roomTemplateId', id)} placeholder="Search templates…" inputStyle={palInputStyle} />
            </div>
          </PalRow>
          <PalRow label="Scope Type" hint={SCOPE_TYPE_DEFINITIONS[draft.scopeType] || 'What portion of a full rebuild of this room this work represents — drives its Budget Room Cost.'}>
            <PalSelect value={draft.scopeType} options={SCOPE_TYPE_OPTIONS} placeholder="Select scope…" onChange={v => setField('scopeType', v)} />
          </PalRow>
          <PalRow label="Room Number">
            <PalText value={draft.roomNumber} placeholder="Optional" onChange={v => setField('roomNumber', v)} />
          </PalRow>
          <PalRow label="Classification">
            <PalVal muted={!(Array.isArray(f['Classification']) ? f['Classification'][0] : f['Classification'])}>
              {(Array.isArray(f['Classification']) ? f['Classification'][0] : f['Classification']) || 'From template'}
            </PalVal>
          </PalRow>
        </PalSection>

        <PalSection title="Areas">
          <PalRow label="Length">
            <PalNum value={draft.length} unit="ft" onChange={v => setField('length', v)} />
          </PalRow>
          <PalRow label="Width">
            <PalNum value={draft.width} unit="ft" onChange={v => setField('width', v)} />
          </PalRow>
          <PalRow label="Manual Override" hint={poly ? "Kept in sync with the room's shape on the plan — edit it there instead." : 'Overrides Length × Width when set (e.g. from a takeoff).'}>
            <PalNum value={draft.areaManual} unit="sf" onChange={v => setField('areaManual', v)} />
          </PalRow>
          <PalRow label="Net Area">
            <PalVal strong muted={!(f['Room Area Net'] > 0)}>{f['Room Area Net'] > 0 ? fmtSF(f['Room Area Net']) : '—'}</PalVal>
          </PalRow>
          <PalRow label="Gross Area" hint="Net × the Control Budget Model's gross area factor.">
            <PalVal muted={!(f['Room Area Gross'] > 0)}>{f['Room Area Gross'] > 0 ? fmtSF(f['Room Area Gross']) : '—'}</PalVal>
          </PalRow>
        </PalSection>

        <PalSection title="Budget">
          <PalRow label="Room Type Baseline" hint={selectedTemplate
            ? (selectedTemplate.structuralBaseFactorNotes || 'Set once per room type in Room Templates — not editable per room.')
            : 'Pick a Room Template.'}>
            <PalVal muted={!selectedTemplate}>
              {selectedTemplate ? pctLabel(selectedTemplate.structuralBaseFactorPct) : 'Pick a Room Template'}
            </PalVal>
          </PalRow>
          <PalRow label="Structural" hint="Case-by-case structural differences this room has that a typical room of its type doesn't (e.g. extra framing for a taller volume) — not finish quality, since the $/SF range below already covers that.">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {Object.keys(STRUCTURAL_CHARACTERISTIC_FACTORS).map(key => (
                <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={draft.structuralCharacteristics.includes(key)}
                    onChange={e => toggleCharacteristic(key, e.target.checked)} />
                  {key} <span style={{ color: 'var(--aj-text-muted)', fontFamily: '"DM Mono",monospace', fontSize: 10.5 }}>{pctLabel(STRUCTURAL_CHARACTERISTIC_FACTORS[key])}</span>
                </label>
              ))}
            </div>
          </PalRow>
          <PalRow label="Cost Factor" hint="Derived from the baseline and structural characteristics above — 1.00× = standard.">
            <PalVal strong>{derivedCostFactor.toFixed(2)}&times;</PalVal>
          </PalRow>
          <PalRow label="Budget Range">
            <PalVal strong muted={!(low > 0 || high > 0)}>{(low > 0 || high > 0) ? fmtUSD(low) + ' – ' + fmtUSD(high) : '—'}</PalVal>
          </PalRow>
          {!(low > 0 || high > 0) && (
            <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 10.5, color: 'var(--aj-text-muted)', lineHeight: 1.45, padding: '2px 14px 0' }}>
              Needs an area, a template classification, and a Control Budget Model on the concept.
            </div>
          )}
        </PalSection>

        <PalSection title="Room Shape">
          {syncErr && (
            <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: '#c0392b', padding: '2px 14px 4px' }}>
              Area not synced to the room book. <button onClick={() => onRetry(room.id)} style={{ background: 'none', border: 'none', color: '#c0392b', cursor: 'pointer', fontSize: 11, padding: 0, textDecoration: 'underline' }}>Retry</button>
            </div>
          )}
          <div className="aj-pm-pal-actions" style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px 0' }}>
            {poly ? (
              <button onClick={() => onCenter(room.id)} className="aj-pm-ghostbtn" style={ghostBtn}>Center on plan</button>
            ) : (
              <button onClick={() => onPlace(room.id)} disabled={!canvasReady || !scaleReady} className="aj-pm-primarybtn"
                style={{ ...primaryBtn, opacity: (!canvasReady || !scaleReady) ? 0.5 : 1 }}
                title={!canvasReady ? 'Upload a plan for this room’s level first' : (!scaleReady ? 'Calibrate the scale first' : undefined)}>
                Draw room
              </button>
            )}
            {poly && (
              <>
                <ActionMenuButton menu={polyMenu} title="More room shape actions" />
                <ActionMenu menu={polyMenu}>
                  <ActionMenuItem onClick={() => onPlace(room.id)}>Redraw room</ActionMenuItem>
                  <ActionMenuItem onClick={() => onDeletePolygon(room.id)} danger title="Removes the shape from the plan only — the room stays in the room book">
                    Delete shape
                  </ActionMenuItem>
                </ActionMenu>
              </>
            )}
          </div>
        </PalSection>
      </div>
    )
  }

  /* ── LEVEL (roll-up) ──────────────────────────────────────────────────── */
  if (selection.kind === 'level') {
    if (!level) return <div style={{ padding: '18px 16px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text-muted)' }}>Loading level…</div>
    const parentUnit = (units || []).find(u => (levelsByUnit[u.id] || []).some(l => l.id === level.id))
    const plan = plansByLevel[level.id]
    const roll = levelRollup(level.id)
    const missingAreas = roll ? roll.rooms - roll.placed : 0
    return (
      <div style={{ paddingBottom: 24 }}>
        <div style={{ padding: '14px 14px 10px' }}>
          <div style={{ ...sideHeading, marginBottom: 3 }}>Level</div>
          <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 14, fontWeight: 600, color: 'var(--aj-text)' }}>{level.fields?.['Name'] || 'Level'}</div>
          {parentUnit && <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: 'var(--aj-text-muted)', marginTop: 2 }}>{parentUnit.fields?.['Unit Type'] || parentUnit.fields?.['Name'] || 'Unit'}</div>}
        </div>

        <PalSection title="Plan" first>
          <PalRow label="Background">
            <PalVal muted={!plan?.pdfUrl}>{plan?.pdfUrl ? (scaleFromPlan(plan) ? 'Uploaded · calibrated' : 'Uploaded · not calibrated') : 'No plan yet'}</PalVal>
          </PalRow>
          {plan && scaleFromPlan(plan) && (
            <PalRow label="Scale Reference">
              <PalVal>{feetLabel(scaleFromPlan(plan).feet)}</PalVal>
            </PalRow>
          )}
          {plan && (
            <PalRow label="Alignment Anchor" hint="A shared point (e.g. a building corner) marked once per level so Views position correctly on every level.">
              <PalVal muted={!plan.anchorPoint}>{plan.anchorPoint ? 'Set ✓' : 'Not set'}</PalVal>
              <button onClick={onSetAnchor} disabled={!canvasReady} className="aj-pm-ghostbtn" style={{ ...ghostBtn, opacity: canvasReady ? 1 : 0.5, marginLeft: 'auto' }}>
                {plan.anchorPoint ? 'Re-set' : 'Set'}
              </button>
            </PalRow>
          )}
          {plan && (
            <PalRow label="Structural Finish Elev." hint="The elevation of the structural finish (e.g. top of slab/subfloor) — NOT the finished-floor elevation (the flooring applied over it). Can be negative for below-grade levels.">
              <input
                type="number" step="any" className="aj-pm-pal-input" value={elevDraft}
                placeholder="—"
                style={palNumStyle}
                onChange={e => setElevDraft(e.target.value)}
                onBlur={() => {
                  const trimmed = elevDraft.trim()
                  const parsed = trimmed === '' ? null : parseFloat(trimmed)
                  const current = levelPlan?.structuralFinishElevation ?? null
                  if (parsed === current || (parsed != null && isNaN(parsed))) { setElevDraft(current != null ? String(current) : ''); return }
                  onSetLevelElevation(plan.id, level.id, parsed)
                }}
              />
              <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 10.5, color: 'var(--aj-text-mid)', flexShrink: 0 }}>ft</span>
            </PalRow>
          )}
        </PalSection>

        <PalSection title="Program roll-up">
          {!roll && <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, color: 'var(--aj-text-muted)', fontStyle: 'italic', padding: '4px 14px' }}>Loading rooms…</div>}
          {roll && (
            <>
              <PalRow label="Rooms"><PalVal>{roll.rooms}</PalVal></PalRow>
              <PalRow label="Rooms Placed"><PalVal muted={!roll.placed}>{roll.placed} of {roll.rooms}</PalVal></PalRow>
              <PalRow label="Net Area"><PalVal strong muted={!(roll.net > 0)}>{roll.net > 0 ? fmtSF(roll.net) : '—'}</PalVal></PalRow>
              <PalRow label="Gross Area"><PalVal muted={!(roll.gross > 0)}>{roll.gross > 0 ? fmtSF(roll.gross) : '—'}</PalVal></PalRow>
            </>
          )}
        </PalSection>

        <PalSection title="Budget roll-up">
          {!roll && <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, color: 'var(--aj-text-muted)', fontStyle: 'italic', padding: '4px 14px' }}>Loading…</div>}
          {roll && (
            <>
              <PalRow label="Budget Low"><PalVal muted={!(roll.low > 0)}>{roll.low > 0 ? fmtUSD(roll.low) : '—'}</PalVal></PalRow>
              <PalRow label="Budget High"><PalVal muted={!(roll.high > 0)}>{roll.high > 0 ? fmtUSD(roll.high) : '—'}</PalVal></PalRow>
              <PalRow label="Range"><PalVal strong muted={!(roll.high > 0)}>{roll.high > 0 ? fmtUSD(roll.low) + ' – ' + fmtUSD(roll.high) : '—'}</PalVal></PalRow>
              {roll.rooms > 0 && missingAreas > 0 && (
                <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 10.5, color: 'var(--aj-text-muted)', lineHeight: 1.45, padding: '4px 14px 0' }}>
                  {missingAreas} room{missingAreas === 1 ? ' has' : 's have'} no shape yet — the roll-up firms up as rooms are placed.
                </div>
              )}
            </>
          )}
        </PalSection>

        <div style={{ padding: '14px 14px 0' }}>
          <button onClick={() => onAddRoom(level.id)} className="aj-pm-primarybtn" style={primaryBtn}>+ Add room to this level</button>
        </div>
      </div>
    )
  }

  /* ── UNIT (roll-up) ───────────────────────────────────────────────────── */
  if (selection.kind === 'unit') {
    if (!unit) return <div style={{ padding: '18px 16px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text-muted)' }}>Loading unit…</div>
    const roll = unitRollup(unit.id)
    return (
      <div style={{ paddingBottom: 24 }}>
        <div style={{ padding: '14px 14px 10px' }}>
          <div style={{ ...sideHeading, marginBottom: 3 }}>Unit</div>
          <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 14, fontWeight: 600, color: 'var(--aj-text)' }}>{unit.fields?.['Unit Type'] || unit.fields?.['Name'] || 'Unit'}</div>
        </div>

        <PalSection title="Program roll-up" first>
          <PalRow label="Levels"><PalVal>{roll.levels}</PalVal></PalRow>
          <PalRow label="Rooms"><PalVal>{roll.rooms}{roll.loading ? '…' : ''}</PalVal></PalRow>
          <PalRow label="Rooms Placed"><PalVal muted={!roll.placed}>{roll.placed} of {roll.rooms}{roll.loading ? '…' : ''}</PalVal></PalRow>
          <PalRow label="Net Area"><PalVal strong muted={!(roll.net > 0)}>{roll.net > 0 ? fmtSF(roll.net) : '—'}</PalVal></PalRow>
          <PalRow label="Gross Area"><PalVal muted={!(roll.gross > 0)}>{roll.gross > 0 ? fmtSF(roll.gross) : '—'}</PalVal></PalRow>
        </PalSection>

        <PalSection title="Budget roll-up">
          <PalRow label="Budget Low"><PalVal muted={!(roll.low > 0)}>{roll.low > 0 ? fmtUSD(roll.low) : '—'}</PalVal></PalRow>
          <PalRow label="Budget High"><PalVal muted={!(roll.high > 0)}>{roll.high > 0 ? fmtUSD(roll.high) : '—'}</PalVal></PalRow>
          <PalRow label="Range"><PalVal strong muted={!(roll.high > 0)}>{roll.high > 0 ? fmtUSD(roll.low) + ' – ' + fmtUSD(roll.high) : '—'}</PalVal></PalRow>
          {roll.loading && (
            <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 10.5, color: 'var(--aj-text-muted)', fontStyle: 'italic', padding: '4px 14px 0' }}>
              Still loading some levels’ rooms — totals will settle in a moment.
            </div>
          )}
        </PalSection>

        <PalSection title="Views">
          {!(viewsByUnit[unit.id] || []).length && (
            <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, color: 'var(--aj-text-muted)', fontStyle: 'italic', padding: '4px 14px' }}>No views yet</div>
          )}
          {!!(viewsByUnit[unit.id] || []).length && (
            <ViewsList unitId={unit.id} views={viewsByUnit[unit.id] || []}
              onDrawInSketch={onDrawViewInSketch} onUpload={onUploadView} onRename={onRenameView} onDelete={onDeleteView}
              onEdit={onEditView}
              onReorder={orderedIds => onReorderViews(unit.id, orderedIds)} />
          )}
          <div style={{ padding: '8px 14px 0' }}>
            <button onClick={() => onAddView(unit.id)} className="aj-pm-ghostbtn" style={{ ...ghostBtn, width: '100%' }}>+ Add view</button>
          </div>
        </PalSection>
      </div>
    )
  }

  return null
}

/** Drag-handle reorder with FLIP animation for the Unit's Views list — mirrors
 *  SiteAnalysisModal's Objects panel (objDragStart / its useLayoutEffect FLIP block) exactly: a
 *  ghost row follows the pointer 1:1 (position:fixed, never recomputed from layout) while the
 *  OTHER rows FLIP-animate into their new slots as the live splice changes their position, so
 *  it's visually obvious where the drop will land. The final order commits to the server once,
 *  on drop (onReorder) — not per-move. */
function ViewsList({ unitId, views, onDrawInSketch, onUpload, onRename, onDelete, onEdit, onReorder }) {
  const [order, setOrder] = useState(() => views.map(v => v.id))
  const draggingRef = useRef(false)
  const rowRefs = useRef({})
  const flipRef = useRef(null)
  const [dragRow, setDragRow] = useState(null)  // { id, rect, dy } | null

  // Resync from the parent's own array whenever it changes for a reason OTHER than our own drag
  // (a view added/deleted, or a reorder committed elsewhere) — never mid-drag, which would fight
  // the live splice below.
  useEffect(() => {
    if (draggingRef.current) return
    setOrder(views.map(v => v.id))
  }, [views])

  useLayoutEffect(() => {
    const before = flipRef.current
    if (!before) return
    flipRef.current = null
    for (const id of Object.keys(before)) {
      const el = rowRefs.current[id]
      if (!el) continue
      const delta = before[id] - el.getBoundingClientRect().top
      if (!delta) continue
      el.style.transition = 'none'
      el.style.transform = 'translateY(' + delta + 'px)'
      requestAnimationFrame(() => { el.style.transition = 'transform 180ms ease'; el.style.transform = '' })
    }
  })

  function dragStart(e, id) {
    e.preventDefault(); e.stopPropagation()
    const rowEl = rowRefs.current[id]
    if (!rowEl) return
    const rect = rowEl.getBoundingClientRect()
    const rowH = rect.height + 2
    const startY = e.clientY
    draggingRef.current = true
    let cur = order
    const startIdx = cur.indexOf(id)
    setDragRow({ id, rect, dy: 0 })
    function move(ev) {
      const dy = ev.clientY - startY
      setDragRow(d => (d && d.id === id ? { ...d, dy } : d))
      const newIdx = Math.max(0, Math.min(cur.length - 1, startIdx + Math.round(dy / rowH)))
      const idxNow = cur.indexOf(id)
      if (newIdx !== idxNow) {
        const before = {}
        for (const id2 of cur) { if (id2 === id) continue; const el = rowRefs.current[id2]; if (el) before[id2] = el.getBoundingClientRect().top }
        flipRef.current = before
        const next = [...cur]
        next.splice(idxNow, 1); next.splice(newIdx, 0, id)
        cur = next
        setOrder(next)
      }
    }
    function up() {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
      setDragRow(null)
      draggingRef.current = false
      onReorder(cur)
    }
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
  }

  const byId = new Map(views.map(v => [v.id, v]))
  const dragged = dragRow && byId.get(dragRow.id)

  return (
    <>
      {order.map(id => {
        const v = byId.get(id)
        if (!v) return null
        return (
          <ViewRow key={id} view={v} rowRef={el => { rowRefs.current[id] = el }}
            dragging={!!(dragRow && dragRow.id === id)}
            onDragHandleDown={e => dragStart(e, id)}
            onDrawInSketch={onDrawInSketch} onUpload={onUpload} onRename={onRename} onDelete={onDelete} onEdit={onEdit} />
        )
      })}
      {dragged && (
        <div style={{
          position: 'fixed', left: dragRow.rect.left, top: dragRow.rect.top + dragRow.dy,
          width: dragRow.rect.width, height: dragRow.rect.height,
          display: 'flex', alignItems: 'center', gap: 8, padding: '6px 14px',
          background: 'var(--aj-bg-elevated)', border: '1px solid var(--aj-accent)', borderRadius: 4,
          boxShadow: '0 6px 18px rgba(7,39,45,0.28)', pointerEvents: 'none', zIndex: 950,
        }}>
          <span style={{ fontSize: 13, flexShrink: 0 }}>{dragged.viewType === 'elevation' ? '⬆' : '✂'}</span>
          <span style={{ flex: 1, fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{dragged.name}</span>
        </div>
      )}
    </>
  )
}

/** One row in the Unit's Views list — drag handle, name, type icon, fed-status, ⋯ menu. */
function ViewRow({ view, rowRef, dragging, onDragHandleDown, onDrawInSketch, onUpload, onRename, onDelete, onEdit }) {
  const menu = useActionMenu()
  const fileInputRef = useRef(null)
  return (
    <div ref={rowRef} className="aj-pm-tree-row" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 14px', opacity: dragging ? 0.35 : 1 }}>
      <span onPointerDown={onDragHandleDown} title="Drag to reorder"
        style={{ cursor: 'grab', color: 'var(--aj-text-muted)', fontSize: 12, padding: '0 2px', touchAction: 'none', flexShrink: 0 }}>⠿</span>
      <span style={{ fontSize: 13, flexShrink: 0 }} title={view.viewType}>{view.viewType === 'elevation' ? '⬆' : '✂'}</span>
      <span style={{ flex: 1, minWidth: 0, fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{view.name}</span>
      {!view.sourceType && <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 9.5, color: 'var(--aj-text-muted)', flexShrink: 0 }}>not fed</span>}
      {view.sourceType === 'upload' && view.uploadUrl && /\.(png|jpe?g|gif|webp)$/i.test(view.uploadUrl) && (
        <img src={view.uploadUrl} alt="" style={{ width: 22, height: 22, objectFit: 'cover', borderRadius: 3, flexShrink: 0 }} />
      )}
      <ActionMenuButton menu={menu} />
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={() => onEdit(view)}>Move / Reorient</ActionMenuItem>
        <ActionMenuDivider />
        <ActionMenuItem onClick={() => onDrawInSketch(view)}>{view.sourceType === 'sketch' ? 'Open in Sketch' : 'Draw in Sketch'}</ActionMenuItem>
        <ActionMenuItem onClick={() => fileInputRef.current?.click()}>Upload image or PDF</ActionMenuItem>
        <ActionMenuDivider />
        <ActionMenuItem onClick={() => onRename(view)}>Rename</ActionMenuItem>
        <ActionMenuItem onClick={() => onDelete(view)} danger>Delete</ActionMenuItem>
      </ActionMenu>
      <input ref={fileInputRef} type="file" accept="image/*,application/pdf" style={{ display: 'none' }}
        onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) onUpload(view, f) }} />
    </div>
  )
}

/* ── Styles ─────────────────────────────────────────────────────────────────── */
function topBtn(active) {
  return {
    background: active ? 'rgba(255,255,255,0.18)' : 'transparent',
    border: '0.5px solid rgba(255,255,255,0.3)', color: '#fff', borderRadius: 4,
    padding: '3px 10px', cursor: 'pointer', fontFamily: '"DM Sans", sans-serif', fontSize: 11.5, fontWeight: 600, lineHeight: 1.5, whiteSpace: 'nowrap',
  }
}
const sideHeading = { fontFamily: '"Montserrat", sans-serif', fontSize: 10, fontWeight: 600, letterSpacing: '0.09em', textTransform: 'uppercase', color: 'var(--aj-text-mid)' }
const drawerMsg = { padding: '8px 14px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text-muted)', fontStyle: 'italic' }
const centerBox = { position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, background: 'var(--aj-bg-muted)' }
const bubbleInput = { width: 56, background: 'var(--aj-bg-surface)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, padding: '5px 7px', color: 'var(--aj-text)', fontFamily: '"DM Mono", monospace', fontSize: 12, outline: 'none' }
const bubbleUnit = { fontFamily: '"DM Mono", monospace', fontSize: 11, color: 'var(--aj-text-mid)' }
const ghostBtn = { background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border-mid)', color: 'var(--aj-text-mid)', borderRadius: 5, padding: '4px 11px', cursor: 'pointer', fontFamily: '"DM Sans", sans-serif', fontSize: 11.5, fontWeight: 600, lineHeight: 1.5 }
const primaryBtn = { background: DARK, border: '0.5px solid ' + DARK, color: '#fff', borderRadius: 5, padding: '4px 12px', cursor: 'pointer', fontFamily: '"DM Sans", sans-serif', fontSize: 11.5, fontWeight: 600, lineHeight: 1.5 }
const miniBtn = { background: 'transparent', border: '0.5px solid var(--aj-border-mid)', color: 'var(--aj-text-mid)', borderRadius: 4, padding: '2px 8px', cursor: 'pointer', fontFamily: '"DM Sans",sans-serif', fontSize: 10.5, fontWeight: 600, lineHeight: 1.5 }
